import React, { useState, useEffect, useCallback, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import axiosInstance from '../utils/axiosConfig';
import { useAuth } from '../contexts/AuthContext';
import '../styles/EmlakSunum.css';
import TrDateInput from '../components/TrDateInput';
import { useTranslation } from 'react-i18next';

// --- Types ---
interface CariInfo {
  cariID: number;
  adiSoyadi: string;
  firmaAdi: string | null;
  telefon1: string;
  telefon2: string | null;
  ePosta: string | null;
  adres: string | null;
}

interface PortfoyFoto {
  fotoID: number;
  dosyaYolu: string;
  isVitrin: boolean;
}

interface PortfoyOzellik {
  ozellikID: number;
  ozellikAdi: string;
  deger: string;
}

interface PortfoyDetay {
  portfoyID: number;
  baslik: string;
  adres: string | null;
  fiyat: number;
  m2: number;
  ilanNo: string;
  tur: string;
  durum: string;
  aktifMi: boolean;
  fotograflar: PortfoyFoto[];
  ozellikler: PortfoyOzellik[];
}

interface SunumPortfoy {
  sunumPortfoyID: number;
  portfoyID: number;
  notAlani: string | null;
  durum: string;
  musteriDurum: string;
  eklenmeTarihi: string;
  guncellemeTarihi: string | null;
  portfoy: PortfoyDetay;
}

interface SunumAkis {
  akisID: number;
  sunumID: number;
  sunumPortfoyID: number | null;
  kullaniciID: number;
  kullaniciAdSoyad: string;
  aciklama: string;
  islemTipi: string;
  eklenmeTarihi: string;
  aktifMi: boolean;
}

interface SunumDetay {
  sunumID: number;
  takipNo: string;
  cariID: number;
  cari: CariInfo;
  olusturanKullaniciID: number;
  olusturanAdSoyad: string;
  baslik: string | null;
  aciklama: string | null;
  durum: string;
  yerGostermeSozlesmesi: string | null;
  olusturmaTarihi: string;
  guncellemeTarihi: string | null;
  aktifMi: boolean;
  portfoyler: SunumPortfoy[];
  akislar: SunumAkis[];
}

interface Not {
  notID: number;
  aciklama: string;
  ekleyen: { adSoyad: string } | null;
  eklenmeTarihi: string;
}

interface Dosya {
  dosyaID: number;
  dosyaAdi: string;
  dosyaYolu: string;
  aciklama: string | null;
  eklenmeTarihi: string;
  ekleyen: { adSoyad: string } | null;
}

interface PaylasimDokuman {
  id: number;
  dokumanID: number;
  baslik: string;
  aciklama?: string;
  dokumanTipi: string;
  dosyaYolu?: string;
  videoEmbedKodu?: string;
  orijinalDosyaAdi?: string;
  etiketler: {ad: string; renk: string}[];
}

interface DokumanItem {
  dokumanID: number;
  baslik: string;
  aciklama?: string;
  dokumanTipi: string;
  dosyaYolu?: string;
  videoEmbedKodu?: string;
  etiketler: {etiketID: number; ad: string; renk: string}[];
}

interface EmlakPortfoyItem {
  portfoyID: number;
  baslik: string;
  fiyat: number;
  m2: number;
  ilanNo: string;
  fotograflar: { fotoID: number; dosyaYolu: string; isVitrin: boolean }[];
}

const portfoyDurumOptions = ['Bekliyor', 'Tanıtıldı', 'Yerinde Gösterildi', 'İptal Edildi'];
const musteriDurumOptions = ['Bekliyor', 'Olumlu', 'Olumsuz', 'Nötr', 'Tekrar Değerlendir'];
const sunumDurumOptions = ['Taslak', 'Devam Ediyor', 'Tamamlandı', 'Olumlu Sonuçlandı', 'Olumsuz Sonuçlandı', 'İptal Edildi'];
const islemTipiOptions = ['Not', 'Arama', 'Görüşme', 'Randevu', 'Gösterim', 'Teklif', 'Durum Değişikliği'];

const durumColors: Record<string, string> = {
  'Taslak': '#94a3b8', 'Devam Ediyor': '#3b82f6', 'Tamamlandı': '#10b981',
  'Olumlu Sonuçlandı': '#059669', 'Olumsuz Sonuçlandı': '#ef4444', 'İptal Edildi': '#6b7280',
  'Bekliyor': '#f59e0b', 'Tanıtıldı': '#3b82f6', 'Yerinde Gösterildi': '#8b5cf6',
  'Olumlu': '#10b981', 'Olumsuz': '#ef4444', 'Nötr': '#94a3b8', 'Tekrar Değerlendirilecek': '#f59e0b'
};

const islemTipiIcons: Record<string, string> = {
  'Not': 'fas fa-sticky-note',
  'Arama': 'fas fa-phone',
  'Görüşme': 'fas fa-comments',
  'Randevu': 'fas fa-calendar-check',
  'Gösterim': 'fas fa-eye',
  'Teklif': 'fas fa-hand-holding-usd',
  'Durum Değişikliği': 'fas fa-exchange-alt',
  'SunumOlusturma': 'fas fa-plus-circle'
};

const EmlakSunumlariDetay: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();

  const [sunum, setSunum] = useState<SunumDetay | null>(null);
  const [notlar, setNotlar] = useState<Not[]>([]);
  const [dosyalar, setDosyalar] = useState<Dosya[]>([]);
  const [loading, setLoading] = useState(true);
  const [islemlerOpen, setIslemlerOpen] = useState(false);

  // Modal states
  const [showAkisModal, setShowAkisModal] = useState(false);
  const [showNotModal, setShowNotModal] = useState(false);
  const [showDosyaModal, setShowDosyaModal] = useState(false);
  const [showPortfoyEkleModal, setShowPortfoyEkleModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [portfoyDeleteConfirm, setPortfoyDeleteConfirm] = useState<number | null>(null);

  // Form states
  const [akisForm, setAkisForm] = useState({ aciklama: '', islemTipi: 'Not', sunumPortfoyID: '' });
  const [notForm, setNotForm] = useState({ aciklama: '' });
  const [dosyaForm, setDosyaForm] = useState<{ dosya: File | null; aciklama: string }>({ dosya: null, aciklama: '' });
  const [editingDurum, setEditingDurum] = useState('');

  // Edit modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editForm, setEditForm] = useState({ aciklama: '', durum: '', yerGostermeSozlesmesi: '' });

  // Döküman Şablonları
  const [dokumanSablonlari, setDokumanSablonlari] = useState<any[]>([]);
  const [showSablonModal, setShowSablonModal] = useState(false);

  // Portföy ekleme
  const [portfoyAramaList, setPortfoyAramaList] = useState<EmlakPortfoyItem[]>([]);
  const [portfoySearch, setPortfoySearch] = useState('');
  const [selectedNewPortfoyler, setSelectedNewPortfoyler] = useState<number[]>([]);

  // Preview state
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewType, setPreviewType] = useState<string>('');

  // Gösterme Belgesi in-app preview
  const [showGostermeBelgesi, setShowGostermeBelgesi] = useState(false);
  const [gbKurumsal, setGbKurumsal] = useState<any>(null);
  const [gbCariDetay, setGbCariDetay] = useState<any>(null);
  const [gbPortfoyFotolar, setGbPortfoyFotolar] = useState<Record<number, string>>({});
  const gostermeBelgesiRef = useRef<HTMLDivElement>(null);

  // Accordion state
  const [sozlesmeOpen, setSozlesmeOpen] = useState(false);

  // QuickView state
  const [qvPortfoyIndex, setQvPortfoyIndex] = useState<number | null>(null);
  const [qvActivePhoto, setQvActivePhoto] = useState(0);

  // Ödeme Planı Modal
  const [showOdemePlaniModal, setShowOdemePlaniModal] = useState<{ sunumPortfoyID: number; portfoyID: number; baslik: string; fiyat: number } | null>(null);
  const [odemePlani, setOdemePlani] = useState<{ teklifID: number | null; teklifMiktari: string; kalemler: { kalemID: number | null; vadeTarihi: string; miktar: string; aciklama: string }[] } | null>(null);
  const [odemePlaniLoading, setOdemePlaniLoading] = useState(false);
  const [odemePlaniSaving, setOdemePlaniSaving] = useState(false);
  const [topluTaksitSayisi, setTopluTaksitSayisi] = useState('12');
  const [topluIlkTarih, setTopluIlkTarih] = useState('');
  const [topluAciklama, setTopluAciklama] = useState('');
  const [topluPlanTutari, setTopluPlanTutari] = useState('');

  // Müşteri Linki Modal
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkler, setLinkler] = useState<{linkID: number; linkToken: string; olusturmaTarihi: string; gecerlilikTarihi: string; sureGectiMi: boolean; portalUrl: string}[]>([]);
  const [linklerLoading, setLinklerLoading] = useState(false);
  const [yeniLinkGun, setYeniLinkGun] = useState('3');
  const [creatingLink, setCreatingLink] = useState(false);
  const [copiedLinkId, setCopiedLinkId] = useState<number | null>(null);
  const [qrVisibleLinkId, setQrVisibleLinkId] = useState<number | null>(null);

  // Online Görüşme
  const [activeGorusme, setActiveGorusme] = useState<{gorusmeID: number; odaAdi: string; baslamaTarihi: string} | null>(null);
  const [gorusmeLoading, setGorusmeLoading] = useState(false);
  const activeGorusmeRef = useRef<number | null>(null);

  // Döküman Paylaşım
  const [showDokumanPaylas, setShowDokumanPaylas] = useState(false);
  const [paylasimDokumanlar, setPaylasimDokumanlar] = useState<PaylasimDokuman[]>([]);
  const [tumDokumanlar, setTumDokumanlar] = useState<DokumanItem[]>([]);
  const [seciliDokumanIDs, setSeciliDokumanIDs] = useState<number[]>([]);
  const [dokumanEtiketFiltre, setDokumanEtiketFiltre] = useState<number | ''>('');
  const [tumEtiketler, setTumEtiketler] = useState<{etiketID: number; ad: string; renk: string}[]>([]);
  const [dokumanPaylasimSaving, setDokumanPaylasimSaving] = useState(false);

  // Binlik ayıraç yardımcı fonksiyonları
  const formatCurrency = (value: string): string => {
    const num = value.replace(/[^\d]/g, '');
    if (!num) return '';
    return parseInt(num).toLocaleString('tr-TR');
  };

  const parseCurrency = (value: string): string => {
    return value.replace(/[^\d]/g, '');
  };

  const fetchSunum = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/api/EmlakSunumlari/${id}`);
      setSunum(response.data);
      if (response.data.durum) setEditingDurum(response.data.durum);
    } catch (error) {
      toast.error(t('msg.islemHatasi'));
      navigate('/emlak-sunumlari');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const fetchDokumanSablonlari = async () => {
    try {
      const response = await axiosInstance.get('/api/DokumanSablonlari');
      setDokumanSablonlari(response.data.filter((s: any) => s.aktifMi));
    } catch (error) {
      console.error('Döküman şablonları yüklenirken hata:', error);
    }
  };

  const fetchNotlar = useCallback(async () => {
    if (!sunum?.takipNo) return;
    try {
      const response = await axiosInstance.get(`/api/Notlar/takip/${sunum.takipNo}`);
      setNotlar(response.data);
    } catch { /* silent */ }
  }, [sunum?.takipNo]);

  const fetchDosyalar = useCallback(async () => {
    if (!sunum?.takipNo) return;
    try {
      const response = await axiosInstance.get(`/api/Dosyalar/takip/${sunum.takipNo}`, { params: { dosyaTuru: 1 } });
      setDosyalar(response.data);
    } catch { /* silent */ }
  }, [sunum?.takipNo]);

  useEffect(() => { fetchSunum(); fetchDokumanSablonlari(); }, [fetchSunum]);
  useEffect(() => { fetchNotlar(); fetchDosyalar(); }, [fetchNotlar, fetchDosyalar]);

  useEffect(() => {
    activeGorusmeRef.current = activeGorusme?.gorusmeID ?? null;
  }, [activeGorusme]);

  useEffect(() => {
    const bitirGorusme = () => {
      const gorusmeID = activeGorusmeRef.current;
      if (!gorusmeID) return;
      const token = (() => {
        try { return JSON.parse(localStorage.getItem('user') || '{}').token; } catch { return null; }
      })();
      const url = `${axiosInstance.defaults.baseURL}/api/OnlineGorusmeler/${gorusmeID}/bitir`;
      if (token) {
        fetch(url, { method: 'PATCH', headers: { Authorization: `Bearer ${token}` }, keepalive: true })
          .catch(() => { navigator.sendBeacon(url, new Blob([], { type: 'application/json' })); });
      }
    };
    window.addEventListener('beforeunload', bitirGorusme);
    return () => {
      window.removeEventListener('beforeunload', bitirGorusme);
      bitirGorusme();
    };
  }, []);

  // --- Handlers ---
  const handleOpenEditModal = () => {
    if (!sunum) return;
    setEditForm({
      aciklama: sunum.aciklama || '',
      durum: sunum.durum,
      yerGostermeSozlesmesi: sunum.yerGostermeSozlesmesi || ''
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    try {
      setEditSaving(true);
      await axiosInstance.put(`/api/EmlakSunumlari/${id}`, {
        aciklama: editForm.aciklama || null,
        durum: editForm.durum,
        yerGostermeSozlesmesi: editForm.yerGostermeSozlesmesi || null
      });
      toast.success(t('msg.islemBasarili'));
      setShowEditModal(false);
      fetchSunum();
    } catch {
      toast.error(t('msg.islemHatasi'));
    } finally {
      setEditSaving(false);
    }
  };

  const handleSablonSec = (sablon: any) => {
    setEditForm({ ...editForm, yerGostermeSozlesmesi: sablon.icerik || '' });
    setShowSablonModal(false);
    toast.success(t('msg.islemBasarili'));
  };

  const handleDurumChange = async (newDurum: string) => {
    try {
      await axiosInstance.put(`/api/EmlakSunumlari/${id}`, { durum: newDurum });
      setEditingDurum(newDurum);
      setSunum(prev => prev ? { ...prev, durum: newDurum } : prev);
      toast.success(t('msg.islemBasarili'));
    } catch {
      toast.error(t('msg.islemHatasi'));
    }
  };

  const handlePortfoyDurumUpdate = async (sunumPortfoyId: number, field: 'durum' | 'musteriDurum' | 'notAlani', value: string) => {
    try {
      // Optimistic update - lokal state'i hemen güncelle
      if (sunum) {
        setSunum({
          ...sunum,
          portfoyler: sunum.portfoyler.map(p =>
            p.sunumPortfoyID === sunumPortfoyId ? { ...p, [field]: value } : p
          )
        });
      }
      const body: any = {};
      body[field] = value;
      body.kullaniciID = user?.kullaniciID;
      await axiosInstance.put(`/api/EmlakSunumlari/${id}/portfoyler/${sunumPortfoyId}`, body);
      // Sessiz yenile (loading göstermeden)
      const response = await axiosInstance.get(`/api/EmlakSunumlari/${id}`);
      setSunum(response.data);
      if (response.data.durum) setEditingDurum(response.data.durum);
    } catch {
      toast.error(t('msg.islemHatasi'));
      fetchSunum();
    }
  };

  const handleAddAkis = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!akisForm.aciklama.trim()) return;
    try {
      await axiosInstance.post(`/api/EmlakSunumlari/${id}/akislar`, {
        kullaniciID: user?.kullaniciID,
        aciklama: akisForm.aciklama,
        islemTipi: akisForm.islemTipi,
        sunumPortfoyID: akisForm.sunumPortfoyID ? parseInt(akisForm.sunumPortfoyID) : null
      });
      toast.success(t('msg.islemBasarili'));
      setShowAkisModal(false);
      setAkisForm({ aciklama: '', islemTipi: 'Not', sunumPortfoyID: '' });
      fetchSunum();
    } catch {
      toast.error(t('msg.islemHatasi'));
    }
  };

  const handleAddNot = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!notForm.aciklama.trim() || !sunum?.takipNo) return;
    try {
      await axiosInstance.post('/api/Notlar', {
        aciklama: notForm.aciklama,
        ekleyenID: user?.kullaniciID,
        aktifMi: true,
        takipNo: sunum.takipNo
      });
      toast.success(t('msg.islemBasarili'));
      setShowNotModal(false);
      setNotForm({ aciklama: '' });
      fetchNotlar();
    } catch {
      toast.error('Not eklenemedi.');
    }
  };

  const handleUploadDosya = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!dosyaForm.dosya || !sunum?.takipNo) return;
    try {
      const formData = new FormData();
      formData.append('file', dosyaForm.dosya);
      formData.append('takipNo', sunum.takipNo);
      formData.append('aciklama', dosyaForm.aciklama);
      await axiosInstance.post('/api/Dosyalar/upload', formData);
      toast.success(t('msg.islemBasarili'));
      setShowDosyaModal(false);
      setDosyaForm({ dosya: null, aciklama: '' });
      fetchDosyalar();
    } catch {
      toast.error(t('msg.islemHatasi'));
    }
  };

  const handleDownloadDosya = async (dosyaID: number, dosyaAdi: string) => {
    try {
      const response = await axiosInstance.get(`/api/Dosyalar/download/${dosyaID}`, {
        responseType: 'blob',
        headers: { 'Accept': 'application/octet-stream' }
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', dosyaAdi);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Dosya indirilemedi.');
    }
  };

  const handlePreviewDosya = async (dosyaID: number, dosyaAdi: string) => {
    const ext = dosyaAdi.split('.').pop()?.toLowerCase() || '';
    const isPdf = ext === 'pdf';
    const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext);
    if (!isPdf && !isImage) {
      handleDownloadDosya(dosyaID, dosyaAdi);
      return;
    }
    try {
      const contentType = isPdf ? 'application/pdf' : `image/${ext === 'jpg' ? 'jpeg' : ext}`;
      const response = await axiosInstance.get(`/api/Dosyalar/download/${dosyaID}`, {
        responseType: 'blob',
        headers: { 'Accept': contentType }
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: contentType }));
      setPreviewUrl(url);
      setPreviewType(isPdf ? 'pdf' : 'image');
    } catch {
      toast.error(t('msg.islemHatasi'));
    }
  };

  const handleDeleteDosya = async (dosyaID: number) => {
    try {
      await axiosInstance.delete(`/api/Dosyalar/${dosyaID}`);
      toast.success(t('msg.islemBasarili'));
      fetchDosyalar();
    } catch {
      toast.error('Dosya silinemedi.');
    }
  };

  const handleDelete = async () => {
    try {
      await axiosInstance.delete(`/api/EmlakSunumlari/${id}`);
      toast.success(t('msg.islemBasarili'));
      navigate('/emlak-sunumlari');
    } catch {
      toast.error(t('msg.islemHatasi'));
    }
  };

  const handlePortfoyDelete = async (sunumPortfoyId: number) => {
    try {
      await axiosInstance.delete(`/api/EmlakSunumlari/${id}/portfoyler/${sunumPortfoyId}`);
      toast.success(t('msg.islemBasarili'));
      setPortfoyDeleteConfirm(null);
      fetchSunum();
    } catch {
      toast.error(t('msg.islemHatasi'));
    }
  };

  const openOdemePlaniModal = async (sp: SunumPortfoy) => {
    if (!sunum) return;
    setShowOdemePlaniModal({ sunumPortfoyID: sp.sunumPortfoyID, portfoyID: sp.portfoyID, baslik: sp.portfoy.baslik, fiyat: sp.portfoy.fiyat });
    setOdemePlaniLoading(true);
    setTopluTaksitSayisi('12');
    setTopluIlkTarih('');
    setTopluAciklama('');
    setTopluPlanTutari('');
    try {
      const res = await axiosInstance.get('/api/SunumGeciTeklifler', { params: { sunumTuru: 'Emlak', sunumID: sunum.sunumID, gayrimenkulID: sp.portfoyID } });
      if (res.data) {
        const teklifMiktar = res.data.teklifMiktari != null ? String(res.data.teklifMiktari) : String(sp.portfoy.fiyat);
        setOdemePlani({ teklifID: res.data.teklifID, teklifMiktari: teklifMiktar, kalemler: res.data.kalemler.map((k: any) => ({ kalemID: k.kalemID, vadeTarihi: k.vadeTarihi, miktar: String(k.miktar), aciklama: k.aciklama || '' })) });
        setTopluPlanTutari(teklifMiktar);
      } else {
        const teklifMiktar = String(sp.portfoy.fiyat);
        setOdemePlani({ teklifID: null, teklifMiktari: teklifMiktar, kalemler: [] });
        setTopluPlanTutari(teklifMiktar);
      }
    } catch {
      toast.error(t('msg.islemHatasi'));
      setShowOdemePlaniModal(null);
    } finally {
      setOdemePlaniLoading(false);
    }
  };

  const handleOdemePlaniSave = async () => {
    if (!sunum || !showOdemePlaniModal || !odemePlani) return;
    setOdemePlaniSaving(true);
    try {
      const teklifMiktarNum = odemePlani.teklifMiktari ? parseFloat(odemePlani.teklifMiktari.replace(/[^\d.]/g, '')) || null : null;
      const upsertRes = await axiosInstance.put('/api/SunumGeciTeklifler', { sunumTuru: 'Emlak', sunumID: sunum.sunumID, gayrimenkulID: showOdemePlaniModal.portfoyID, teklifMiktari: teklifMiktarNum });
      const teklifID = upsertRes.data.teklifID;
      const kalemlerPayload = odemePlani.kalemler.map((k, i) => ({ vadeTarihi: k.vadeTarihi, miktar: parseFloat(k.miktar || '0'), aciklama: k.aciklama || null, sira: i }));
      await axiosInstance.post(`/api/SunumGeciTeklifler/${teklifID}/kalemler/toplu`, kalemlerPayload);
      toast.success(t('msg.islemBasarili'));
      setShowOdemePlaniModal(null);
    } catch {
      toast.error(t('msg.islemHatasi'));
    } finally {
      setOdemePlaniSaving(false);
    }
  };

  const handleTopluPlanOlustur = () => {
    if (!odemePlani || !topluIlkTarih || !topluTaksitSayisi || !topluPlanTutari) return;
    const sayisi = parseInt(topluTaksitSayisi);
    const planTutari = parseFloat(topluPlanTutari.replace(/[^\d.]/g, ''));
    if (!sayisi || !planTutari) return;
    const taksitMiktari = Math.floor(planTutari / sayisi);
    const yeniKalemler: { kalemID: number | null; vadeTarihi: string; miktar: string; aciklama: string }[] = [];
    const ilkTarih = new Date(topluIlkTarih);
    const sonTarih = new Date(ilkTarih);
    sonTarih.setMonth(sonTarih.getMonth() + sayisi - 1);
    const ortalamaVadeGun = Math.round((sonTarih.getTime() - ilkTarih.getTime()) / (1000 * 60 * 60 * 24) / 2);
    for (let i = 0; i < sayisi; i++) {
      const vade = new Date(ilkTarih);
      vade.setMonth(vade.getMonth() + i);
      yeniKalemler.push({ kalemID: null, vadeTarihi: vade.toISOString().slice(0, 10), miktar: i < sayisi - 1 ? String(taksitMiktari) : String(planTutari - taksitMiktari * (sayisi - 1)), aciklama: topluAciklama ? `${topluAciklama} ${i + 1}` : `${i + 1}. Taksit` });
    }
    setOdemePlani(prev => prev ? { ...prev, kalemler: yeniKalemler } : prev);
    toast.success(t('msg.islemBasarili'));
  };

  const fetchLinkler = async () => {
    if (!sunum) return;
    setLinklerLoading(true);
    try {
      const res = await axiosInstance.get(`/api/SunumMusteriLinks/Emlak/${sunum.sunumID}`);
      setLinkler(res.data);
    } catch {
      toast.error(t('msg.islemHatasi'));
    } finally {
      setLinklerLoading(false);
    }
  };

  const handleCreateLink = async () => {
    if (!sunum) return;
    setCreatingLink(true);
    try {
      await axiosInstance.post('/api/SunumMusteriLinks', { 
        sunumTuru: 'Emlak', 
        sunumID: sunum.sunumID, 
        gunSayisi: parseInt(yeniLinkGun) || 3,
        frontendUrl: window.location.origin
      });
      toast.success(t('msg.islemBasarili'));
      fetchLinkler();
    } catch {
      toast.error(t('msg.islemHatasi'));
    } finally {
      setCreatingLink(false);
    }
  };

  const handleExtendLink = async (linkID: number) => {
    try {
      await axiosInstance.put(`/api/SunumMusteriLinks/${linkID}/uzat`, { ekGunSayisi: 3 });
      toast.success(t('msg.islemBasarili'));
      fetchLinkler();
    } catch {
      toast.error(t('msg.islemHatasi'));
    }
  };

  const handleDeactivateLink = async (linkID: number) => {
    try {
      await axiosInstance.delete(`/api/SunumMusteriLinks/${linkID}`);
      toast.success(t('msg.islemBasarili'));
      fetchLinkler();
    } catch {
      toast.error(t('msg.islemHatasi'));
    }
  };

  const handleCopyLink = (linkID: number, url: string) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopiedLinkId(linkID);
      setTimeout(() => setCopiedLinkId(null), 2000);
    });
  };

  const handleBaslatGorusme = async () => {
    if (!sunum) return;
    setGorusmeLoading(true);
    try {
      const res = await axiosInstance.post('/api/OnlineGorusmeler/baslat', { sunumID: sunum.sunumID, sunumTuru: 'Emlak' });
      setActiveGorusme({ gorusmeID: res.data.gorusmeID, odaAdi: res.data.odaAdi, baslamaTarihi: res.data.baslamaTarihi });
      toast.success(t('msg.islemBasarili'));
      const w = window.screen.width;
      const h = window.screen.height;
      window.open(
        res.data.jitsiUrl,
        'JitsiGorusme',
        `toolbar=no,menubar=no,location=no,status=no,scrollbars=no,resizable=yes,width=${Math.round(w * 0.85)},height=${Math.round(h * 0.85)},left=${Math.round(w * 0.075)},top=${Math.round(h * 0.075)}`
      );
    } catch {
      toast.error(t('msg.islemHatasi'));
    } finally {
      setGorusmeLoading(false);
    }
  };

  const handleBitirGorusme = async () => {
    if (!activeGorusme) return;
    setGorusmeLoading(true);
    try {
      await axiosInstance.patch(`/api/OnlineGorusmeler/${activeGorusme.gorusmeID}/bitir`);
      setActiveGorusme(null);
      toast.success(t('msg.islemBasarili'));
    } catch {
      toast.error(t('msg.islemHatasi'));
    } finally {
      setGorusmeLoading(false);
    }
  };

  const fetchPaylasimDokumanlar = useCallback(async (sunumID: number) => {
    try {
      const res = await axiosInstance.get(`/api/EmlakSunumPaylasimDokumanlar/${sunumID}`);
      setPaylasimDokumanlar(res.data);
    } catch { /* sessiz */ }
  }, []);

  const openDokumanModal = async () => {
    if (!sunum) return;
    setShowDokumanPaylas(true);
    try {
      const [dokRes, etiketRes, paylasimRes] = await Promise.all([
        axiosInstance.get('/api/Dokumanlar'),
        axiosInstance.get('/api/Etiketler'),
        axiosInstance.get(`/api/EmlakSunumPaylasimDokumanlar/${sunum.sunumID}`),
      ]);
      setTumDokumanlar(dokRes.data);
      setTumEtiketler(etiketRes.data);
      setPaylasimDokumanlar(paylasimRes.data);
      setSeciliDokumanIDs((paylasimRes.data as PaylasimDokuman[]).map(p => p.dokumanID));
    } catch { toast.error(t('msg.islemHatasi')); }
  };

  const handleDokumanPaylasimKaydet = async () => {
    if (!sunum) return;
    setDokumanPaylasimSaving(true);
    try {
      await axiosInstance.post(`/api/EmlakSunumPaylasimDokumanlar/${sunum.sunumID}`, seciliDokumanIDs);
      await fetchPaylasimDokumanlar(sunum.sunumID);
      toast.success(t('msg.islemBasarili'));
      setShowDokumanPaylas(false);
    } catch { toast.error(t('msg.islemHatasi')); }
    finally { setDokumanPaylasimSaving(false); }
  };

  const openPortfoyEkleModal = async () => {
    setShowPortfoyEkleModal(true);
    setSelectedNewPortfoyler([]);
    setPortfoySearch('');
    try {
      const response = await axiosInstance.get('/api/EmlakPortfoy?aktifMi=true');
      setPortfoyAramaList(response.data);
    } catch {
      toast.error(t('msg.islemHatasi'));
    }
  };

  const handlePortfoyEkle = async () => {
    if (selectedNewPortfoyler.length === 0) return;
    try {
      await axiosInstance.post(`/api/EmlakSunumlari/${id}/portfoyler`, {
        portfoyIDler: selectedNewPortfoyler
      });
      toast.success(t('msg.islemBasarili'));
      setShowPortfoyEkleModal(false);
      fetchSunum();
    } catch {
      toast.error(t('msg.islemHatasi'));
    }
  };

  const openQuickView = (portfoyID: number) => {
    if (!sunum) return;
    const idx = sunum.portfoyler.findIndex(p => p.portfoyID === portfoyID);
    if (idx >= 0) {
      setQvPortfoyIndex(idx);
      setQvActivePhoto(0);
    }
  };

  const qvNavigate = (dir: number) => {
    if (qvPortfoyIndex === null || !sunum) return;
    const newIdx = qvPortfoyIndex + dir;
    if (newIdx >= 0 && newIdx < sunum.portfoyler.length) {
      setQvPortfoyIndex(newIdx);
      setQvActivePhoto(0);
    }
  };

  const generateTasinmazGostermeBelgesi = async () => {
    if (!sunum) return;
    try {
      let kurumsal: any = null;
      try { const r = await axiosInstance.get('/api/Kurumsal/1'); kurumsal = r.data; } catch {}

      let cariDetay: any = null;
      try { const r = await axiosInstance.get(`/api/Cariler/${sunum.cariID}`); cariDetay = r.data; } catch {}

      // Logo base64
      let logoBase64 = '';
      if (kurumsal?.logo) {
        try {
          const logoRes = await axiosInstance.get(`/userFiles/${kurumsal.logo}`, { responseType: 'blob', baseURL: '' });
          logoBase64 = await new Promise<string>((resolve) => {
            const reader = new FileReader(); reader.onloadend = () => resolve(reader.result as string); reader.readAsDataURL(logoRes.data);
          });
        } catch {
          try {
            const logoRes2 = await fetch(`${axiosInstance.defaults.baseURL}/userFiles/${kurumsal.logo}`);
            const blob = await logoRes2.blob();
            logoBase64 = await new Promise<string>((resolve) => {
              const reader = new FileReader(); reader.onloadend = () => resolve(reader.result as string); reader.readAsDataURL(blob);
            });
          } catch {}
        }
      }

      // Portföy vitrin fotoğrafları base64
      const fotolar: Record<number, string> = {};
      for (const sp of sunum.portfoyler) {
        const vitrin = sp.portfoy.fotograflar?.find(f => f.isVitrin) || sp.portfoy.fotograflar?.[0];
        if (vitrin) {
          try {
            const fRes = await fetch(`${axiosInstance.defaults.baseURL}/api/EmlakPortfoy/foto/${vitrin.dosyaYolu}`);
            const blob = await fRes.blob();
            fotolar[sp.portfoyID] = await new Promise<string>((resolve) => {
              const reader = new FileReader(); reader.onloadend = () => resolve(reader.result as string); reader.readAsDataURL(blob);
            });
          } catch {}
        }
      }

      if (kurumsal) kurumsal._logoBase64 = logoBase64;
      setGbKurumsal(kurumsal);
      setGbCariDetay(cariDetay);
      setGbPortfoyFotolar(fotolar);
      setShowGostermeBelgesi(true);
    } catch {
      toast.error(t('msg.islemHatasi'));
    }
  };

  const downloadGostermeBelgesiPDF = async () => {
    if (!gostermeBelgesiRef.current) return;
    toast.info(t('msg.bilgi'), { autoClose: 2000 });
    try {
      const el = gostermeBelgesiRef.current;
      const prevBoxShadow = el.style.boxShadow; const prevBorder = el.style.border; const prevBorderRadius = el.style.borderRadius;
      el.style.boxShadow = 'none'; el.style.border = 'none'; el.style.borderRadius = '0';
      const canvas = await html2canvas(el, { scale: 2.5, useCORS: true, backgroundColor: '#ffffff', logging: false });
      el.style.boxShadow = prevBoxShadow; el.style.border = prevBorder; el.style.borderRadius = prevBorderRadius;
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const margin = 10;
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const printableW = pageWidth - margin * 2;
      const printableH = pageHeight - margin * 2;
      const pxPerMm = canvas.width / printableW;
      const sliceHeightPx = Math.floor(printableH * pxPerMm);
      const totalPages = Math.ceil(canvas.height / sliceHeightPx);
      for (let page = 0; page < totalPages; page++) {
        if (page > 0) pdf.addPage();
        const srcY = page * sliceHeightPx;
        const srcH = Math.min(sliceHeightPx, canvas.height - srcY);
        const sliceCanvas = document.createElement('canvas');
        sliceCanvas.width = canvas.width; sliceCanvas.height = srcH;
        const ctx = sliceCanvas.getContext('2d');
        if (ctx) { ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height); ctx.drawImage(canvas, 0, srcY, canvas.width, srcH, 0, 0, canvas.width, srcH); }
        pdf.addImage(sliceCanvas.toDataURL('image/png'), 'PNG', margin, margin, printableW, srcH / pxPerMm);
      }
      pdf.save(`TasinmazGostermeBelgesi_${sunum?.takipNo || 'belge'}.pdf`);
      toast.success('PDF indirildi!');
    } catch { toast.error(t('msg.islemHatasi')); }
  };

  if (showGostermeBelgesi && sunum) {
    const k = gbKurumsal;
    const c = gbCariDetay;
    const tarih = new Date().toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    const turLabels: Record<string, string> = { 'Konut': t('emlak.konut'), 'IsYeri': t('emlak.isYeri'), 'Arsa': t('emlak.arsa'), 'Tarla': 'Tarla' };
    const sozlesmeMetni = sunum.yerGostermeSozlesmesi?.replace(/&nbsp;/g, ' ');
    return (
      <div id="gb-root">
        <style>{`
          @media print {
            @page { size: A4; margin: 0; }
            .gb-toolbar { display: none !important; }
            .gb-bg { background: white !important; padding: 0 !important; }
            .gb-paper {
              box-shadow: none !important; margin: 0 !important; border-radius: 0 !important;
              max-width: 100% !important; min-height: unset !important; border: none !important;
              padding: 12mm 10mm !important; width: 100% !important;
            }
            tr { break-inside: avoid; page-break-inside: avoid; }
            thead { display: table-header-group; }
            table { page-break-inside: auto; }
          }
        `}</style>

        {/* Toolbar */}
        <div className="gb-toolbar" style={{position:'sticky',top:0,zIndex:50,background:'rgba(255,255,255,0.92)',backdropFilter:'blur(8px)',borderBottom:'1px solid #e5e7eb',padding:'12px 24px',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
          <button onClick={() => setShowGostermeBelgesi(false)} style={{display:'flex',alignItems:'center',gap:6,background:'none',border:'none',color:'#6b7280',fontSize:13,fontWeight:500,cursor:'pointer'}}
            onMouseEnter={e => (e.currentTarget as HTMLButtonElement).style.color='#111827'}
            onMouseLeave={e => (e.currentTarget as HTMLButtonElement).style.color='#6b7280'}>
            {t('btn.geriDon')}
          </button>
          <div style={{display:'flex',gap:10}}>
            <button onClick={downloadGostermeBelgesiPDF} style={{display:'flex',alignItems:'center',gap:7,background:'#fff',border:'1px solid #d1d5db',color:'#374151',borderRadius:8,padding:'8px 18px',fontSize:13,fontWeight:500,cursor:'pointer'}}
              onMouseEnter={e => (e.currentTarget as HTMLButtonElement).style.background='#f9fafb'}
              onMouseLeave={e => (e.currentTarget as HTMLButtonElement).style.background='#fff'}>
              <i className="fas fa-file-pdf" style={{color:'#dc2626'}}></i> PDF Olarak Kaydet
            </button>
            <button onClick={() => window.print()} style={{display:'flex',alignItems:'center',gap:7,background:'#cd6906',border:'none',color:'#fff',borderRadius:8,padding:'8px 18px',fontSize:13,fontWeight:500,cursor:'pointer',boxShadow:'0 1px 3px rgba(205,105,6,0.3)'}}
              onMouseEnter={e => (e.currentTarget as HTMLButtonElement).style.background='#a85505'}
              onMouseLeave={e => (e.currentTarget as HTMLButtonElement).style.background='#cd6906'}>
              <i className="fas fa-print"></i>{t('btn.yazdir')}
            </button>
          </div>
        </div>

        {/* BG */}
        <div className="gb-bg" style={{background:'rgba(243,244,246,0.8)',minHeight:'100vh',padding:'32px 16px'}}>
          <div ref={gostermeBelgesiRef} className="gb-paper" style={{background:'#fff',boxShadow:'0 20px 60px rgba(0,0,0,0.14)',margin:'0 auto',padding:'15mm 10mm',width:'100%',maxWidth:'210mm',minHeight:'297mm',borderRadius:2,boxSizing:'border-box',border:'1px solid #e5e7eb',fontFamily:"'Inter','Segoe UI',sans-serif"}}>

            {/* Header */}
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',paddingBottom:16,borderBottom:'2px solid #111827',marginBottom:20}}>
              <div style={{display:'flex',flexDirection:'column',gap:8}}>
                {k?._logoBase64
                  ? <img src={k._logoBase64} alt="logo" style={{maxHeight:48,maxWidth:150,objectFit:'contain',marginBottom:4}} />
                  : k?.firmaAdi && <div style={{fontSize:16,fontWeight:900,color:'#111827'}}>{k.firmaAdi}</div>
                }
                {k && (
                  <div style={{fontSize:9,color:'#6b7280',lineHeight:1.7}}>
                    {k.adres && <div>{k.adres}{k.sehir ? ' / '+k.sehir : ''}</div>}
                    {k.yetkiNo && <div>Yetki Belge No: {k.yetkiNo}</div>}
                    {k.tel && <div>Tel: {k.tel}</div>}
                  </div>
                )}
              </div>
              <div style={{textAlign:'right'}}>
                <div style={{fontSize:16,fontWeight:900,color:'#111827',letterSpacing:'-0.5px',textTransform:'uppercase',lineHeight:1.1}}>TAŞINMAZ<br />GÖSTERME<br />BELGESİ</div>
                <div style={{marginTop:8,fontSize:9,color:'#6b7280',lineHeight:1.7}}>
                  <div>Form No: <span style={{fontWeight:700,color:'#111827'}}>{sunum.takipNo}</span></div>
                  <div>Tarih: <span style={{fontWeight:700,color:'#111827'}}>{tarih}</span></div>
                </div>
              </div>
            </div>

            {/* Müşteri & Danışman */}
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:16}}>
              <div style={{border:'1px solid #e5e7eb',borderRadius:10,padding:'12px 14px'}}>
                <div style={{fontSize:9,fontWeight:800,textTransform:'uppercase',letterSpacing:'0.1em',color:'#9ca3af',marginBottom:8}}>Taşınmazı Gören / Aday Müşteri</div>
                {[['Ad Soyad', c?.adiSoyadi || sunum.cari.adiSoyadi],['T.C. Kimlik No', c?.tc],['Telefon', c?.telefon1 || sunum.cari.telefon1],['E-Posta', c?.ePosta || sunum.cari.ePosta],['Adres', c?.adres || sunum.cari.adres]]
                  .filter(r => r[1]).map(([lbl, val]) => (
                    <div key={lbl as string} style={{display:'flex',gap:8,marginBottom:3,fontSize:10}}>
                      <span style={{fontSize:9,fontWeight:700,color:'#9ca3af',minWidth:80,flexShrink:0,textTransform:'uppercase',letterSpacing:'0.04em'}}>{lbl}</span>
                      <span style={{fontWeight:600,color:'#111827'}}>{val}</span>
                    </div>
                ))}
              </div>
              <div style={{border:'1px solid #e5e7eb',borderRadius:10,padding:'12px 14px'}}>
                <div style={{fontSize:9,fontWeight:800,textTransform:'uppercase',letterSpacing:'0.1em',color:'#9ca3af',marginBottom:8}}>Gayrimenkul Danışmanı / Ofis</div>
                {[[t('label.danisман'), sunum.olusturanAdSoyad],[t('label.ofis'), k?.firmaAdi],[t('emlak.yetkiBelgeNo'), k?.yetkiNo],['Vergi Dairesi', k?.vd],['Vergi No', k?.vn]]
                  .filter(r => r[1]).map(([lbl, val]) => (
                    <div key={lbl as string} style={{display:'flex',gap:8,marginBottom:3,fontSize:10}}>
                      <span style={{fontSize:9,fontWeight:700,color:'#9ca3af',minWidth:80,flexShrink:0,textTransform:'uppercase',letterSpacing:'0.04em'}}>{lbl}</span>
                      <span style={{fontWeight:600,color:'#111827'}}>{val}</span>
                    </div>
                ))}
              </div>
            </div>

            {/* Portföyler */}
            <div style={{marginBottom:16}}>
              <div style={{fontSize:9,fontWeight:800,textTransform:'uppercase',letterSpacing:'0.08em',color:'#cd6906',borderBottom:'1.5px solid #cd6906',paddingBottom:4,marginBottom:0}}>Gösterilen Taşınmazlar ({sunum.portfoyler.length})</div>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:9.5}}>
                <thead>
                  <tr style={{background:'#f9fafb',borderBottom:'2px solid #e5e7eb'}}>
                    {[t('tablo.sira'), t('label.gorsel'), t('label.baslik'), t('label.tur'), t('emlak.ilanNo')].map((h,i) => (
                      <th key={h} style={{padding:'8px 10px',textAlign: i===0||i>2 ? 'center':'left',fontSize:9,fontWeight:700,color:'#6b7280',textTransform:'uppercase',letterSpacing:'0.04em'}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sunum.portfoyler.map((sp, idx) => (
                    <tr key={sp.sunumPortfoyID} style={{borderBottom:'1px solid #f1f5f9',background: idx%2===0 ? '#fff':'#fafafa'}}>
                      <td style={{padding:'8px 10px',textAlign:'center',color:'#9ca3af',fontSize:9,fontWeight:700}}>{idx+1}</td>
                      <td style={{padding:'6px 10px',textAlign:'center'}}>
                        {gbPortfoyFotolar[sp.portfoyID]
                          ? <img src={gbPortfoyFotolar[sp.portfoyID]} alt="" style={{width:80,height:56,objectFit:'cover',borderRadius:4,border:'1px solid #e5e7eb'}} />
                          : <div style={{width:80,height:56,background:'#f1f5f9',borderRadius:4,display:'inline-flex',alignItems:'center',justifyContent:'center',color:'#cbd5e1',fontSize:10}}>—</div>}
                      </td>
                      <td style={{padding:'8px 10px'}}>
                        <div style={{fontWeight:700,color:'#111827',fontSize:10}}>{sp.portfoy.baslik}</div>
                        {sp.portfoy.adres && <div style={{fontSize:9,color:'#9ca3af',marginTop:1}}>{sp.portfoy.adres}</div>}
                      </td>
                      <td style={{padding:'8px 10px',textAlign:'center',color:'#374151'}}>{turLabels[sp.portfoy.tur] || sp.portfoy.tur}</td>
                      <td style={{padding:'8px 10px',textAlign:'center',fontFamily:'monospace',fontSize:9,color:'#6b7280'}}>{sp.portfoy.ilanNo}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Yasal Metin / Sözleşme */}
            {sozlesmeMetni ? (
              <div style={{marginBottom:16}}>
                <div style={{fontSize:9,fontWeight:800,textTransform:'uppercase',letterSpacing:'0.08em',color:'#9ca3af',borderBottom:'1px solid #e5e7eb',paddingBottom:4,marginBottom:10}}>Yer Gösterme Sözleşmesi</div>
                <div style={{fontSize:10,lineHeight:1.8,color:'#4b5563',textAlign:'justify',fontStyle:'italic',background:'#f9fafb',borderRadius:8,padding:'12px 14px',border:'1px solid #e5e7eb'}}
                  dangerouslySetInnerHTML={{__html: sozlesmeMetni}} />
              </div>
            ) : (
              <div style={{marginBottom:16,background:'#f9fafb',borderRadius:8,padding:'12px 14px',border:'1px solid #e5e7eb',fontSize:10,lineHeight:1.8,color:'#4b5563',textAlign:'justify',fontStyle:'italic'}}>
                <p>İşbu yer gösterme formu, yukarıda kimlik bilgileri verilen aday müşteriye, belirtilen tarihte, aşağıda listelenen taşınmazların <strong style={{fontStyle:'normal',color:'#111827'}}>gösterildiğini</strong> ve bu hizmet karşılığında taraflar arasında ayrıca bir sözleşme düzenleneceğini taahhüt eder.</p>
                <p style={{marginTop:8}}>Müşteri, bu formu imzalamakla gösterilen taşınmazların listesini teyit etmiş olup, söz konusu taşınmazlara ilişkin olası bir satış, kiralama veya benzeri bir işlemde yetkili aracının bilgisi dışında ayrı bir anlaşma yapmayacağını kabul ve taahhüt eder.</p>
              </div>
            )}

            {/* İmza */}
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:48,marginTop:32,paddingTop:24,borderTop:'1px solid #e5e7eb'}}>
              {[{title:'MÜŞTERİ İMZASI',name: c?.adiSoyadi || sunum.cari.adiSoyadi},{title:'OFİS / DANIŞMAN İMZASI',name: sunum.olusturanAdSoyad}].map(s => (
                <div key={s.title}>
                  <div style={{fontSize:9,fontWeight:800,textTransform:'uppercase',letterSpacing:'0.08em',color:'#111827',marginBottom:4}}>{s.title}</div>
                  <div style={{fontSize:10,fontWeight:600,color:'#6b7280',marginBottom:12}}>{s.name}</div>
                  <div style={{height:48,borderBottom:'1.5px dashed #cbd5e1',marginBottom:8}}></div>
                  <div style={{fontSize:8.5,color:'#9ca3af'}}>İmza &nbsp;&nbsp; Tarih: _____ / _____ / _________</div>
                </div>
              ))}
            </div>

            {/* Footer */}
            <div style={{marginTop:20,borderTop:'1px solid #e5e7eb',paddingTop:8,textAlign:'center',fontSize:8,color:'#9ca3af'}}>
              {k?.firmaAdi ? k.firmaAdi+'  |  ' : ''}{k?.adres || ''}{k?.sehir ? ' / '+k.sehir : ''}{k && '  |  '}Bu belge {tarih} tarihinde düzenlenmiştir.
            </div>

          </div>
        </div>
      </div>
    );
  }

  const qvPortfoy = qvPortfoyIndex !== null && sunum ? sunum.portfoyler[qvPortfoyIndex] : null;

  const fotoUrl = (dosyaYolu: string) => `${axiosInstance.defaults.baseURL}/api/EmlakPortfoy/foto/${dosyaYolu}`;

  const getVitrinFoto = (fotograflar: PortfoyFoto[]): string | null => {
    const vitrin = fotograflar?.find(f => f.isVitrin);
    if (vitrin) return vitrin.dosyaYolu;
    if (fotograflar?.length > 0) return fotograflar[0].dosyaYolu;
    return null;
  };

  const formatDate = (dateStr: string) => new Date(dateStr).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const formatDateTime = (dateStr: string) => new Date(dateStr).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  const getDosyaIcon = (dosyaAdi: string) => {
    const ext = dosyaAdi.split('.').pop()?.toLowerCase() || '';
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return 'fas fa-file-image';
    if (ext === 'pdf') return 'fas fa-file-pdf';
    if (['doc', 'docx'].includes(ext)) return 'fas fa-file-word';
    if (['xls', 'xlsx'].includes(ext)) return 'fas fa-file-excel';
    return 'fas fa-file';
  };

  if (loading) return <div className="es-loading"><i className="fas fa-spinner fa-spin"></i> Yükleniyor...</div>;
  if (!sunum) return null;

  const existingPortfoyIds = sunum.portfoyler.map(p => p.portfoyID);
  const filteredNewPortfoyler = portfoyAramaList
    .filter(p => !existingPortfoyIds.includes(p.portfoyID))
    .filter(p => !portfoySearch || p.baslik.toLowerCase().includes(portfoySearch.toLowerCase()) || p.ilanNo.toLowerCase().includes(portfoySearch.toLowerCase()));

  return (
    <div className="es-container">
      {/* Header */}
      <div className="es-header">
        <div className="es-header-left">
          <button
            onClick={() => navigate(-1)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '12px', fontWeight: 600, color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer', padding: 0, transition: 'color 0.15s', letterSpacing: '0.01em', flexShrink: 0 }}
            onMouseEnter={e => (e.currentTarget.style.color = '#0f172a')}
            onMouseLeave={e => (e.currentTarget.style.color = '#94a3b8')}
          >
            <i className="fas fa-arrow-left" style={{ fontSize: '10px' }}></i> Geri Dön
          </button>
          <div>
            <h1 className="es-title">{sunum.baslik || sunum.cari.adiSoyadi + ' Sunumu'}</h1>
            <span className="es-count">{sunum.takipNo}</span>
          </div>
        </div>
        <div className="es-header-actions">
          <select
            className="es-durum-select"
            value={editingDurum}
            onChange={e => handleDurumChange(e.target.value)}
            style={{ borderColor: durumColors[editingDurum] || '#94a3b8', color: durumColors[editingDurum] || '#94a3b8' }}
          >
            {sunumDurumOptions.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          <div className="es-islemler-dropdown">
            <button className="es-btn-primary es-btn-sm" onClick={() => setIslemlerOpen(prev => !prev)}>
              <i className="fas fa-ellipsis-v"></i> İşlemler <i className={`fas fa-chevron-${islemlerOpen ? 'up' : 'down'}`} style={{ fontSize: 10, marginLeft: 4 }}></i>
            </button>
            {islemlerOpen && (
              <>
                <div className="es-islemler-backdrop" onClick={() => setIslemlerOpen(false)} />
                <div className="es-islemler-menu">
                  <button className="es-islemler-item" onClick={() => { setIslemlerOpen(false); handleOpenEditModal(); }}>
                    <i className="fas fa-edit"></i> Düzenle
                  </button>
                  <button className="es-islemler-item" onClick={() => { setIslemlerOpen(false); generateTasinmazGostermeBelgesi(); }}>
                    <i className="fas fa-file-pdf"></i> Taşınmaz Gösterme Belgesi
                  </button>
                  <button className="es-islemler-item es-islemler-item-danger" onClick={() => { setIslemlerOpen(false); setDeleteConfirm(true); }}>
                    <i className="fas fa-trash"></i> Sil
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Müşteri Info */}
      <div className="es-hero">
        <div className="es-hero-section">
          <h3 className="es-hero-label"><i className="fas fa-user"></i> Müşteri Bilgileri</h3>
          <div className="es-hero-grid">
            <div className="es-hero-item">
              <span className="es-hero-item-label">Ad Soyad</span>
              <span className="es-hero-item-value">{sunum.cari.adiSoyadi}</span>
            </div>
            {sunum.cari.firmaAdi && (
              <div className="es-hero-item">
                <span className="es-hero-item-label">Firma</span>
                <span className="es-hero-item-value">{sunum.cari.firmaAdi}</span>
              </div>
            )}
            <div className="es-hero-item">
              <span className="es-hero-item-label">Telefon</span>
              <span className="es-hero-item-value">{sunum.cari.telefon1}{sunum.cari.telefon2 ? ` / ${sunum.cari.telefon2}` : ''}</span>
            </div>
            {sunum.cari.ePosta && (
              <div className="es-hero-item">
                <span className="es-hero-item-label">E-posta</span>
                <span className="es-hero-item-value">{sunum.cari.ePosta}</span>
              </div>
            )}
            {sunum.cari.adres && (
              <div className="es-hero-item">
                <span className="es-hero-item-label">Adres</span>
                <span className="es-hero-item-value">{sunum.cari.adres}</span>
              </div>
            )}
          </div>
        </div>
        <div className="es-hero-section">
          <h3 className="es-hero-label"><i className="fas fa-info-circle"></i> Sunum Bilgileri</h3>
          <div className="es-hero-grid">
            <div className="es-hero-item">
              <span className="es-hero-item-label">Durum</span>
              <span className="es-hero-badge" style={{ backgroundColor: (durumColors[sunum.durum] || '#94a3b8') + '15', color: durumColors[sunum.durum] || '#94a3b8' }}>
                {sunum.durum}
              </span>
            </div>
            <div className="es-hero-item">
              <span className="es-hero-item-label">Oluşturan</span>
              <span className="es-hero-item-value">{sunum.olusturanAdSoyad}</span>
            </div>
            <div className="es-hero-item">
              <span className="es-hero-item-label">Oluşturma Tarihi</span>
              <span className="es-hero-item-value">{formatDateTime(sunum.olusturmaTarihi)}</span>
            </div>
            {sunum.aciklama && (
              <div className="es-hero-item es-hero-item-full">
                <span className="es-hero-item-label">{t('label.aciklama')}</span>
                <span className="es-hero-item-value">{sunum.aciklama}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Yer Gösterme Sözleşmesi */}
      {sunum.yerGostermeSozlesmesi && (
        <div className="es-section">
          <div className="es-section-header es-section-header-toggle" onClick={() => setSozlesmeOpen(prev => !prev)} style={{ cursor: 'pointer', userSelect: 'none' }}>
            <h2 className="es-section-title"><i className="fas fa-file-contract"></i> Yer Gösterme Sözleşmesi</h2>
            <i className={`fas fa-chevron-${sozlesmeOpen ? 'up' : 'down'} es-accordion-icon`}></i>
          </div>
          {sozlesmeOpen && (
            <div className="es-sozlesme-content" dangerouslySetInnerHTML={{ __html: sunum.yerGostermeSozlesmesi.replace(/&nbsp;/g, ' ') }} />
          )}
        </div>
      )}

      {/* Portföyler */}
      <div className="es-section">
        <div className="es-section-header">
          <h2 className="es-section-title"><i className="fas fa-building"></i> Portföyler ({sunum.portfoyler.length})</h2>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className="es-btn-secondary es-btn-sm"
              onClick={openDokumanModal}
              title="Döküman Paylaş"
              style={{ background: 'rgba(255,255,255,0.12)', color: '#1a2440', border: '1px solid rgba(26,36,64,0.25)' }}
            >
              <i className="fas fa-file-export"></i>
            </button>
            {activeGorusme ? (
              <button
                className="es-btn-secondary es-btn-sm"
                onClick={handleBitirGorusme}
                disabled={gorusmeLoading}
                title="Görüşmeyi Bitir"
                style={{ background: '#ef4444', color: '#fff', border: 'none', opacity: gorusmeLoading ? 0.7 : 1 }}
              >
                <i className="fas fa-video-slash"></i>
              </button>
            ) : (
              <button
                className="es-btn-secondary es-btn-sm"
                onClick={handleBaslatGorusme}
                disabled={gorusmeLoading}
                title="Online Görüşme Başlat"
                style={{ background: 'rgba(255,255,255,0.12)', color: '#1a2440', border: '1px solid rgba(26,36,64,0.25)', opacity: gorusmeLoading ? 0.7 : 1 }}
              >
                <i className="fas fa-video"></i>
              </button>
            )}
            <button
              className="es-btn-secondary es-btn-sm"
              onClick={() => { setShowLinkModal(true); fetchLinkler(); }}
              style={{ background: 'rgba(255,255,255,0.12)', color: '#1a2440', border: '1px solid rgba(26,36,64,0.25)' }}
            >
              <i className="fas fa-link"></i>
            </button>
            <button className="es-btn-primary es-btn-sm" onClick={openPortfoyEkleModal} title={t('btn.ekle')}>
              <i className="fas fa-plus"></i>
            </button>
          </div>
        </div>
        <div className="es-portfoy-list">
          {sunum.portfoyler.map(sp => {
            const foto = getVitrinFoto(sp.portfoy.fotograflar);
            return (
              <div key={sp.sunumPortfoyID} className="es-sp-card">
                <div className="es-sp-card-img" onClick={() => openQuickView(sp.portfoyID)}>
                  {foto ? <img src={fotoUrl(foto)} alt="" /> : <i className="fas fa-image"></i>}
                </div>
                <div className="es-sp-card-body">
                  <div className="es-sp-card-top">
                    <div>
                      <h4 className="es-sp-card-title" onClick={() => openQuickView(sp.portfoyID)}>{sp.portfoy.baslik}</h4>
                      <div className="es-sp-card-meta">
                        <span>{sp.portfoy.ilanNo}</span>
                        <span>{sp.portfoy.fiyat.toLocaleString('tr-TR')} ₺</span>
                        <span>{sp.portfoy.m2} m²</span>
                        <span>{sp.portfoy.tur}</span>
                      </div>
                    </div>
                    <div className="es-sp-card-top-actions">
                      <div className="es-sp-card-controls">
                        <div className="es-sp-control">
                          <label>Sunum Durumu</label>
                          <select
                            value={sp.durum}
                            onChange={e => handlePortfoyDurumUpdate(sp.sunumPortfoyID, 'durum', e.target.value)}
                            style={{ borderColor: durumColors[sp.durum], color: durumColors[sp.durum] }}
                          >
                            {portfoyDurumOptions.map(d => <option key={d} value={d}>{d}</option>)}
                          </select>
                        </div>
                        <div className="es-sp-control">
                          <label>Müşteri Tepkisi</label>
                          <select
                            value={sp.musteriDurum}
                            onChange={e => handlePortfoyDurumUpdate(sp.sunumPortfoyID, 'musteriDurum', e.target.value)}
                            style={{ borderColor: durumColors[sp.musteriDurum], color: durumColors[sp.musteriDurum] }}
                          >
                            {musteriDurumOptions.map(d => <option key={d} value={d}>{d}</option>)}
                          </select>
                        </div>
                      </div>
                      <button className="es-btn-icon" onClick={() => openOdemePlaniModal(sp)} title="Ödeme Planı"
                        style={{ color: '#e67d21' }}>
                        <i className="fas fa-calendar-alt"></i>
                      </button>
                      <button className="es-btn-icon es-btn-icon-danger" onClick={() => setPortfoyDeleteConfirm(sp.sunumPortfoyID)} title="Kaldır">
                        <i className="fas fa-times"></i>
                      </button>
                    </div>
                  </div>
                  <div className="es-sp-card-not">
                    <textarea
                      placeholder={t('label.notlar')}
                      defaultValue={sp.notAlani || ''}
                      onBlur={e => {
                        if (e.target.value !== (sp.notAlani || '')) {
                          handlePortfoyDurumUpdate(sp.sunumPortfoyID, 'notAlani', e.target.value);
                        }
                      }}
                      rows={2}
                    />
                  </div>
                </div>
              </div>
            );
          })}
          {sunum.portfoyler.length === 0 && (
            <div className="es-empty-sm">Henüz portföy eklenmemiş.</div>
          )}
        </div>
      </div>

      {/* Akış & Dosyalar Row */}
      <div className="es-two-col">
        {/* Akış */}
        <div className="es-section-box">
          <div className="es-section-box-header">
            <h3><i className="fas fa-stream"></i> Sunum Akışı</h3>
            <button className="es-btn-primary es-btn-sm" onClick={() => setShowAkisModal(true)}>
              <i className="fas fa-plus"></i> Ekle
            </button>
          </div>
          <div className="es-timeline-container">
            {sunum.akislar.length === 0 ? (
              <div className="es-empty-sm">Henüz akış kaydı yok.</div>
            ) : (
              <div className="es-timeline">
                <div className="es-timeline-line"></div>
                {sunum.akislar.map((akis, index) => (
                  <div key={akis.akisID} className="es-timeline-item" style={{ paddingBottom: index === sunum.akislar.length - 1 ? 0 : '1rem' }}>
                    <div className="es-timeline-dot" style={{ borderColor: durumColors[akis.islemTipi] || '#4f46e5' }}></div>
                    <div className="es-timeline-content">
                      <div className="es-timeline-meta">
                        <span className="es-timeline-type">
                          <i className={islemTipiIcons[akis.islemTipi] || 'fas fa-circle'}></i> {akis.islemTipi}
                        </span>
                        <span>{akis.kullaniciAdSoyad} • {formatDateTime(akis.eklenmeTarihi)}</span>
                      </div>
                      <div className="es-timeline-text">{akis.aciklama}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Notlar & Dosyalar */}
        <div className="es-section-box">
          {/* Notlar */}
          <div className="es-section-box-header">
            <h3><i className="fas fa-sticky-note"></i> Notlar</h3>
            <button className="es-btn-primary es-btn-sm" onClick={() => setShowNotModal(true)}>
              <i className="fas fa-plus"></i> Ekle
            </button>
          </div>
          <div className="es-notes-container">
            {notlar.length === 0 ? (
              <div className="es-empty-sm">Not bulunmamaktadır.</div>
            ) : (
              notlar.map(not => (
                <div key={not.notID} className="es-note-item">
                  <div className="es-note-meta">{not.ekleyen?.adSoyad} • {formatDateTime(not.eklenmeTarihi)}</div>
                  <div className="es-note-text">{not.aciklama}</div>
                </div>
              ))
            )}
          </div>

          {/* Dosyalar */}
          <div className="es-section-box-header" style={{ marginTop: '1.5rem' }}>
            <h3><i className="fas fa-paperclip"></i> Dosyalar</h3>
            <button className="es-btn-primary es-btn-sm" onClick={() => setShowDosyaModal(true)}>
              <i className="fas fa-upload"></i> Yükle
            </button>
          </div>
          <div className="es-files-container">
            {dosyalar.length === 0 ? (
              <div className="es-empty-sm">Dosya bulunmamaktadır.</div>
            ) : (
              dosyalar.map(dosya => (
                <div key={dosya.dosyaID} className="es-file-item">
                  <div className="es-file-icon"><i className={getDosyaIcon(dosya.dosyaAdi)}></i></div>
                  <div className="es-file-info">
                    <div className="es-file-name">{dosya.dosyaAdi}</div>
                    <div className="es-file-meta">{dosya.ekleyen?.adSoyad} • {formatDate(dosya.eklenmeTarihi)}</div>
                  </div>
                  <div className="es-file-actions">
                    <button onClick={() => handlePreviewDosya(dosya.dosyaID, dosya.dosyaAdi)} title="Önizle"><i className="fas fa-eye"></i></button>
                    <button onClick={() => handleDownloadDosya(dosya.dosyaID, dosya.dosyaAdi)} title={t('btn.indir')}><i className="fas fa-download"></i></button>
                    <button onClick={() => handleDeleteDosya(dosya.dosyaID)} title={t('btn.sil')} className="es-file-btn-danger"><i className="fas fa-trash"></i></button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Akis Modal */}
      {showAkisModal && (
        <div className="es-modal-overlay" onClick={() => setShowAkisModal(false)}>
          <div className="es-modal" onClick={e => e.stopPropagation()}>
            <div className="es-modal-header">
              <span className="es-modal-title"><i className="fas fa-stream" style={{ color: '#cd6906', marginRight: 8 }}></i>Akış Kaydı Ekle</span>
              <button className="es-modal-close" onClick={() => setShowAkisModal(false)}>✕</button>
            </div>
            <form onSubmit={handleAddAkis}>
              <div className="es-modal-body">
                <div className="es-form-group">
                  <label className="es-label">İşlem Tipi</label>
                  <select className="es-input" value={akisForm.islemTipi} onChange={e => setAkisForm(p => ({ ...p, islemTipi: e.target.value }))}>
                    {islemTipiOptions.map(item => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div className="es-form-group">
                  <label className="es-label">İlgili Portföy <span className="es-optional">(opsiyonel)</span></label>
                  <select className="es-input" value={akisForm.sunumPortfoyID} onChange={e => setAkisForm(p => ({ ...p, sunumPortfoyID: e.target.value }))}>
                    <option value="">Genel</option>
                    {sunum.portfoyler.map(sp => (
                      <option key={sp.sunumPortfoyID} value={sp.sunumPortfoyID}>{sp.portfoy.baslik} ({sp.portfoy.ilanNo})</option>
                    ))}
                  </select>
                </div>
                <div className="es-form-group">
                  <label className="es-label">Açıklama <span className="es-required">*</span></label>
                  <textarea className="es-textarea" rows={4} value={akisForm.aciklama} onChange={e => setAkisForm(p => ({ ...p, aciklama: e.target.value }))} placeholder={t('label.aciklama')} required />
                </div>
              </div>
              <div className="es-modal-footer">
                <button type="button" className="es-btn-cancel" onClick={() => setShowAkisModal(false)}>{t('btn.iptal')}</button>
                <button type="submit" className="es-btn-primary"><i className="fas fa-check"></i> Kaydet</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Not Modal */}
      {showNotModal && (
        <div className="es-modal-overlay" onClick={() => setShowNotModal(false)}>
          <div className="es-modal" onClick={e => e.stopPropagation()}>
            <div className="es-modal-header">
              <span className="es-modal-title"><i className="fas fa-sticky-note" style={{ color: '#cd6906', marginRight: 8 }}></i>Not Ekle</span>
              <button className="es-modal-close" onClick={() => setShowNotModal(false)}>✕</button>
            </div>
            <form onSubmit={handleAddNot}>
              <div className="es-modal-body">
                <div className="es-form-group">
                  <label className="es-label">Not <span className="es-required">*</span></label>
                  <textarea className="es-textarea" rows={4} value={notForm.aciklama} onChange={e => setNotForm({ aciklama: e.target.value })} placeholder={t('label.notlar')} required />
                </div>
              </div>
              <div className="es-modal-footer">
                <button type="button" className="es-btn-cancel" onClick={() => setShowNotModal(false)}>{t('btn.iptal')}</button>
                <button type="submit" className="es-btn-primary"><i className="fas fa-check"></i> Kaydet</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Dosya Modal */}
      {showDosyaModal && (
        <div className="es-modal-overlay" onClick={() => setShowDosyaModal(false)}>
          <div className="es-modal" onClick={e => e.stopPropagation()}>
            <div className="es-modal-header">
              <span className="es-modal-title"><i className="fas fa-upload" style={{ color: '#cd6906', marginRight: 8 }}></i>Dosya Yükle</span>
              <button className="es-modal-close" onClick={() => setShowDosyaModal(false)}>✕</button>
            </div>
            <form onSubmit={handleUploadDosya}>
              <div className="es-modal-body">
                <div className="es-form-group">
                  <label className="es-label">Dosya <span className="es-required">*</span></label>
                  <input type="file" className="es-input" onChange={e => setDosyaForm(p => ({ ...p, dosya: e.target.files?.[0] || null }))} required />
                </div>
                <div className="es-form-group">
                  <label className="es-label">Açıklama <span className="es-optional">(opsiyonel)</span></label>
                  <input type="text" className="es-input" value={dosyaForm.aciklama} onChange={e => setDosyaForm(p => ({ ...p, aciklama: e.target.value }))} placeholder={t('label.aciklama')} />
                </div>
              </div>
              <div className="es-modal-footer">
                <button type="button" className="es-btn-cancel" onClick={() => setShowDosyaModal(false)}>{t('btn.iptal')}</button>
                <button type="submit" className="es-btn-primary"><i className="fas fa-upload"></i> Yükle</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Portföy Ekle Modal */}
      {showPortfoyEkleModal && (
        <div className="es-modal-overlay" onClick={() => setShowPortfoyEkleModal(false)}>
          <div className="es-modal es-modal-lg" onClick={e => e.stopPropagation()}>
            <div className="es-modal-header">
              <span className="es-modal-title"><i className="fas fa-plus-circle" style={{ color: '#cd6906', marginRight: 8 }}></i>Portföy Ekle</span>
              <button className="es-modal-close" onClick={() => setShowPortfoyEkleModal(false)}>✕</button>
            </div>
            <div className="es-modal-body">
              <input type="text" className="es-input" placeholder={t('label.ara')} value={portfoySearch} onChange={e => setPortfoySearch(e.target.value)} />
              <div className="es-portfoy-grid" style={{ marginTop: '1rem' }}>
                {filteredNewPortfoyler.slice(0, 30).map(p => {
                  const isSelected = selectedNewPortfoyler.includes(p.portfoyID);
                  const foto = p.fotograflar?.find(f => f.isVitrin)?.dosyaYolu || p.fotograflar?.[0]?.dosyaYolu;
                  return (
                    <div key={p.portfoyID} className={`es-portfoy-item ${isSelected ? 'selected' : ''}`} onClick={() => {
                      setSelectedNewPortfoyler(prev => prev.includes(p.portfoyID) ? prev.filter(x => x !== p.portfoyID) : [...prev, p.portfoyID]);
                    }}>
                      <div className="es-portfoy-item-img">
                        {foto ? <img src={fotoUrl(foto)} alt="" /> : <i className="fas fa-image"></i>}
                      </div>
                      <div className="es-portfoy-item-info">
                        <div className="es-portfoy-item-title">{p.baslik}</div>
                        <div className="es-portfoy-item-meta">{p.ilanNo} • {p.fiyat.toLocaleString('tr-TR')} ₺ • {p.m2} m²</div>
                      </div>
                      <div className="es-portfoy-item-check">{isSelected && <i className="fas fa-check-circle"></i>}</div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="es-modal-footer">
              <button className="es-btn-cancel" onClick={() => setShowPortfoyEkleModal(false)}>{t('btn.iptal')}</button>
              <button className="es-btn-primary" onClick={handlePortfoyEkle} disabled={selectedNewPortfoyler.length === 0}>
                <i className="fas fa-check"></i> {selectedNewPortfoyler.length} Portföy Ekle
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className="es-modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="es-modal es-modal-lg" onClick={e => e.stopPropagation()}>
            <div className="es-modal-header">
              <span className="es-modal-title"><i className="fas fa-edit" style={{ color: '#cd6906', marginRight: 8 }}></i>Sunum Düzenle</span>
              <button className="es-modal-close" onClick={() => setShowEditModal(false)}>✕</button>
            </div>
            <div className="es-modal-body">
              <div className="es-form-group">
                <label className="es-label">Durum</label>
                <select className="es-input" value={editForm.durum} onChange={e => setEditForm({ ...editForm, durum: e.target.value })}>
                  {sunumDurumOptions.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div className="es-form-group">
                <label className="es-label">{t('label.aciklama')}</label>
                <textarea className="es-textarea" rows={3} value={editForm.aciklama} onChange={e => setEditForm({ ...editForm, aciklama: e.target.value })} placeholder={t('label.aciklama')} />
              </div>
              <div className="es-form-group">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <label className="es-label" style={{ marginBottom: 0 }}>Yer Gösterme Sözleşmesi</label>
                  <button
                    type="button"
                    className="es-btn-primary es-btn-sm"
                    style={{ fontSize: 12, padding: '4px 10px' }}
                    onClick={() => setShowSablonModal(true)}
                  >
                    <i className="fas fa-file-alt"></i> Şablondan Getir
                  </button>
                </div>
                <ReactQuill
                  theme="snow"
                  value={editForm.yerGostermeSozlesmesi}
                  onChange={(value) => setEditForm({ ...editForm, yerGostermeSozlesmesi: value })}
                  modules={{
                    toolbar: [
                      [{ 'header': [1, 2, 3, false] }],
                      ['bold', 'italic', 'underline', 'strike'],
                      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                      [{ 'indent': '-1'}, { 'indent': '+1' }],
                      ['link'],
                      ['clean']
                    ]
                  }}
                  formats={['header', 'bold', 'italic', 'underline', 'strike', 'list', 'bullet', 'indent', 'link']}
                  placeholder={t('label.ara')}
                />
              </div>
            </div>
            <div className="es-modal-footer">
              <button type="button" className="es-btn-cancel" onClick={() => setShowEditModal(false)}>{t('btn.iptal')}</button>
              <button type="button" className="es-btn-primary" onClick={handleSaveEdit} disabled={editSaving}>
                {editSaving ? <><i className="fas fa-spinner fa-spin"></i> Kaydediliyor...</> : <><i className="fas fa-check"></i> Kaydet</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Döküman Şablonları Seçim Modal */}
      {showSablonModal && (
        <div className="es-modal-overlay" onClick={() => setShowSablonModal(false)} style={{ zIndex: 1060 }}>
          <div className="es-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 600 }}>
            <div className="es-modal-header">
              <span className="es-modal-title"><i className="fas fa-file-alt" style={{ color: '#0ea5e9', marginRight: 8 }}></i>Döküman Şablonu Seç</span>
              <button className="es-modal-close" onClick={() => setShowSablonModal(false)}>✕</button>
            </div>
            <div className="es-modal-body" style={{ padding: 0 }}>
              {dokumanSablonlari.length === 0 ? (
                <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>
                  <i className="fas fa-inbox" style={{ fontSize: 48, marginBottom: 16, opacity: 0.5 }}></i>
                  <p style={{ margin: 0, fontSize: 14, fontWeight: 500 }}>Henüz şablon eklenmemiş</p>
                  <p style={{ margin: '4px 0 0 0', fontSize: 12 }}>Tanımlamalar &rsaquo; Döküman Şablonları menüsünden şablon ekleyebilirsiniz.</p>
                </div>
              ) : (
                <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                  {dokumanSablonlari.map((sablon: any) => (
                    <div
                      key={sablon.dokumanSablonID}
                      style={{ padding: '14px 20px', borderBottom: '1px solid #f1f5f9', cursor: 'pointer', transition: 'background 0.15s' }}
                      onClick={() => handleSablonSec(sablon)}
                      onMouseOver={e => (e.currentTarget.style.background = '#f8fafc')}
                      onMouseOut={e => (e.currentTarget.style.background = '')}
                    >
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a', marginBottom: 4 }}>
                        <i className="fas fa-file-alt" style={{ color: '#0ea5e9', fontSize: 12, marginRight: 6 }}></i>
                        {sablon.sablonBasligi}
                      </div>
                      {sablon.icerik && (
                        <div style={{ fontSize: 12, color: '#94a3b8', lineHeight: 1.4 }}>
                          {sablon.icerik.replace(/<[^>]*>/g, '').substring(0, 100)}...
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="es-modal-footer">
              <button type="button" className="es-btn-cancel" onClick={() => setShowSablonModal(false)}>{t('btn.kapat')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteConfirm && (
        <div className="es-modal-overlay" onClick={() => setDeleteConfirm(false)}>
          <div className="es-modal" onClick={e => e.stopPropagation()}>
            <div className="es-modal-header">
              <span className="es-modal-title"><i className="fas fa-exclamation-triangle" style={{ color: '#dc2626', marginRight: 8 }}></i>Sunumu Sil</span>
              <button className="es-modal-close" onClick={() => setDeleteConfirm(false)}>✕</button>
            </div>
            <div className="es-modal-body"><p>Bu sunumu ve tüm ilişkili verileri silmek istediğinize emin misiniz?</p></div>
            <div className="es-modal-footer">
              <button className="es-btn-cancel" onClick={() => setDeleteConfirm(false)}>{t('btn.iptal')}</button>
              <button className="es-btn-danger" onClick={handleDelete}><i className="fas fa-trash"></i> Sil</button>
            </div>
          </div>
        </div>
      )}

      {/* Portföy Delete Confirm */}
      {portfoyDeleteConfirm && (
        <div className="es-modal-overlay" onClick={() => setPortfoyDeleteConfirm(null)}>
          <div className="es-modal" onClick={e => e.stopPropagation()}>
            <div className="es-modal-header">
              <span className="es-modal-title"><i className="fas fa-exclamation-triangle" style={{ color: '#dc2626', marginRight: 8 }}></i>Portföy Kaldır</span>
              <button className="es-modal-close" onClick={() => setPortfoyDeleteConfirm(null)}>✕</button>
            </div>
            <div className="es-modal-body"><p>Bu portföyü sunumdan kaldırmak istediğinize emin misiniz?</p></div>
            <div className="es-modal-footer">
              <button className="es-btn-cancel" onClick={() => setPortfoyDeleteConfirm(null)}>{t('btn.iptal')}</button>
              <button className="es-btn-danger" onClick={() => handlePortfoyDelete(portfoyDeleteConfirm)}><i className="fas fa-trash"></i> Kaldır</button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {previewUrl && (
        <div className="es-modal-overlay" onClick={() => { setPreviewUrl(null); }}>
          <div className="es-modal es-modal-preview" onClick={e => e.stopPropagation()}>
            <div className="es-modal-header">
              <span className="es-modal-title">Önizleme</span>
              <button className="es-modal-close" onClick={() => setPreviewUrl(null)}>✕</button>
            </div>
            <div className="es-modal-body" style={{ padding: 0, textAlign: 'center' }}>
              {previewType === 'pdf' ? (
                <iframe src={previewUrl} style={{ width: '100%', height: '70vh', border: 'none' }} title="PDF Preview" />
              ) : (
                <img src={previewUrl} alt="Preview" style={{ maxWidth: '100%', maxHeight: '70vh' }} />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Portföy QuickView Modal */}
      {qvPortfoy && qvPortfoyIndex !== null && sunum && (
        <div className="es-modal-overlay es-qv-overlay" onClick={() => setQvPortfoyIndex(null)}>
          <div className="es-qv-modal" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="es-qv-header">
              <div className="es-qv-header-left">
                <span className="es-qv-counter">{qvPortfoyIndex + 1} / {sunum.portfoyler.length}</span>
                <h2 className="es-qv-title">{qvPortfoy.portfoy.baslik}</h2>
              </div>
              <div className="es-qv-header-right">
                <button className="es-qv-header-btn es-qv-nav-btn" onClick={() => qvNavigate(-1)} disabled={qvPortfoyIndex === 0} title="Önceki">
                  <i className="fas fa-chevron-left"></i>
                </button>
                <button className="es-qv-header-btn es-qv-nav-btn" onClick={() => qvNavigate(1)} disabled={qvPortfoyIndex === sunum.portfoyler.length - 1} title="Sonraki">
                  <i className="fas fa-chevron-right"></i>
                </button>
                <span className="es-qv-header-sep"></span>
                <button className="es-qv-header-btn" onClick={() => navigate(`/emlak-portfoy/${qvPortfoy.portfoyID}`)} title="Detay Sayfası">
                  <i className="fas fa-external-link-alt"></i>
                </button>
                <button className="es-qv-header-btn es-qv-close" onClick={() => setQvPortfoyIndex(null)}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="es-qv-body">
              {/* Gallery */}
              <div className="es-qv-gallery">
                {qvPortfoy.portfoy.fotograflar.length > 0 ? (
                  <>
                    <div className="es-qv-gallery-main">
                      <img src={fotoUrl(qvPortfoy.portfoy.fotograflar[qvActivePhoto].dosyaYolu)} alt={qvPortfoy.portfoy.baslik} />
                      {qvPortfoy.portfoy.fotograflar.length > 1 && (
                        <>
                          <button className="es-qv-gallery-nav es-qv-gallery-nav-prev" onClick={() => setQvActivePhoto(prev => prev === 0 ? qvPortfoy.portfoy.fotograflar.length - 1 : prev - 1)}>
                            <i className="fas fa-chevron-left"></i>
                          </button>
                          <button className="es-qv-gallery-nav es-qv-gallery-nav-next" onClick={() => setQvActivePhoto(prev => prev === qvPortfoy.portfoy.fotograflar.length - 1 ? 0 : prev + 1)}>
                            <i className="fas fa-chevron-right"></i>
                          </button>
                        </>
                      )}
                    </div>
                    {qvPortfoy.portfoy.fotograflar.length > 1 && (
                      <div className="es-qv-thumbs">
                        {qvPortfoy.portfoy.fotograflar.map((foto, idx) => (
                          <div key={foto.fotoID} className={`es-qv-thumb ${idx === qvActivePhoto ? 'active' : ''}`} onClick={() => setQvActivePhoto(idx)}>
                            <img src={fotoUrl(foto.dosyaYolu)} alt="" />
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="es-qv-no-photo"><i className="fas fa-image"></i><p>Fotoğraf yok</p></div>
                )}
              </div>

              {/* Info */}
              <div className="es-qv-info">
                <div className="es-qv-price">{qvPortfoy.portfoy.fiyat.toLocaleString('tr-TR')} ₺</div>
                {qvPortfoy.portfoy.m2 > 0 && (
                  <div className="es-qv-price-m2">{Math.round(qvPortfoy.portfoy.fiyat / qvPortfoy.portfoy.m2).toLocaleString('tr-TR')} ₺/m²</div>
                )}

                <div className="es-qv-badges">
                  <span className="es-qv-badge" style={{ backgroundColor: (durumColors[qvPortfoy.durum] || '#94a3b8') + '15', color: durumColors[qvPortfoy.durum] || '#94a3b8' }}>
                    {qvPortfoy.durum}
                  </span>
                  <span className="es-qv-badge" style={{ backgroundColor: (durumColors[qvPortfoy.musteriDurum] || '#94a3b8') + '15', color: durumColors[qvPortfoy.musteriDurum] || '#94a3b8' }}>
                    {qvPortfoy.musteriDurum}
                  </span>
                </div>

                <div className="es-qv-details">
                  <div className="es-qv-detail-item">
                    <span className="es-qv-detail-label"><i className="fas fa-ruler-combined"></i> Alan</span>
                    <span className="es-qv-detail-value">{qvPortfoy.portfoy.m2} m²</span>
                  </div>
                  <div className="es-qv-detail-item">
                    <span className="es-qv-detail-label"><i className="fas fa-hashtag"></i> İlan No</span>
                    <span className="es-qv-detail-value">{qvPortfoy.portfoy.ilanNo}</span>
                  </div>
                  <div className="es-qv-detail-item">
                    <span className="es-qv-detail-label"><i className="fas fa-home"></i> Tür</span>
                    <span className="es-qv-detail-value">{qvPortfoy.portfoy.tur}</span>
                  </div>
                  {qvPortfoy.portfoy.adres && (
                    <div className="es-qv-detail-item">
                      <span className="es-qv-detail-label"><i className="fas fa-map-marker-alt"></i> Adres</span>
                      <span className="es-qv-detail-value">{qvPortfoy.portfoy.adres}</span>
                    </div>
                  )}
                </div>

                {qvPortfoy.portfoy.ozellikler.length > 0 && (
                  <div className="es-qv-section">
                    <h3 className="es-qv-section-title"><i className="fas fa-tags"></i> Özellikler</h3>
                    <div className="es-qv-tags">
                      {qvPortfoy.portfoy.ozellikler.map(oz => (
                        <span key={oz.ozellikID} className="es-qv-tag"><i className="fas fa-check"></i> {oz.ozellikAdi}</span>
                      ))}
                    </div>
                  </div>
                )}

                {qvPortfoy.notAlani && (
                  <div className="es-qv-section">
                    <h3 className="es-qv-section-title"><i className="fas fa-sticky-note"></i> Portföy Notu</h3>
                    <p className="es-qv-note-text">{qvPortfoy.notAlani}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Ödeme Planı Modalı */}
      {showOdemePlaniModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1400, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
          onClick={() => setShowOdemePlaniModal(null)}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(2px)' }} />
          <div style={{ position: 'relative', width: '96%', maxWidth: 1000, maxHeight: '90vh', background: '#fff', borderRadius: 16, overflow: 'hidden', boxShadow: '0 24px 64px rgba(0,0,0,0.25)', display: 'flex', flexDirection: 'column' }}
            onClick={e => e.stopPropagation()}>

            {/* Header */}
            <div style={{ padding: '16px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#1a2440', flexShrink: 0 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#fff' }}>
                  <i className="fas fa-calendar-alt" style={{ marginRight: 8, color: '#e67d21' }}></i>
                  Ödeme Planı — {showOdemePlaniModal.baslik}
                </div>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 3 }}>
                  İlan Fiyatı: {showOdemePlaniModal.fiyat.toLocaleString('tr-TR')} ₺
                </div>
              </div>
              <button onClick={() => setShowOdemePlaniModal(null)}
                style={{ background: 'rgba(255,255,255,0.1)', border: 'none', cursor: 'pointer', padding: '5px 7px', color: '#fff', borderRadius: 8, display: 'flex', alignItems: 'center', fontSize: 18, fontWeight: 700 }}>
                ×
              </button>
            </div>

            {/* Body */}
            <div style={{ flex: 1, overflowY: 'auto', padding: 20, display: 'flex', flexDirection: 'column', gap: 18 }}>
              {odemePlaniLoading ? (
                <div style={{ textAlign: 'center', color: '#64748b', padding: '2rem 0' }}>
                  <i className="fas fa-spinner fa-spin" style={{ marginRight: 8 }}></i>Yükleniyor...
                </div>
              ) : odemePlani && (
                <>
                  {/* Teklif Miktarı */}
                  <div style={{ background: '#fff8f0', border: '1px solid #fed7aa', borderRadius: 12, padding: '14px 16px' }}>
                    <label style={{ fontSize: 12, fontWeight: 700, color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: 8 }}>
                      <i className="fas fa-hand-holding-usd" style={{ marginRight: 6 }}></i>Teklif Miktarı
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input
                        type="text"
                        value={formatCurrency(odemePlani.teklifMiktari)}
                        onChange={e => setOdemePlani(prev => prev ? { ...prev, teklifMiktari: parseCurrency(e.target.value) } : prev)}
                        placeholder={t('label.tutar')}
                        style={{ flex: 1, padding: '8px 12px', border: '1px solid #fbbf24', borderRadius: 8, fontSize: 15, fontWeight: 600, color: '#e67d21', outline: 'none', background: '#fff' }}
                      />
                      <span style={{ fontSize: 15, fontWeight: 700, color: '#e67d21' }}>₺</span>
                    </div>
                  </div>

                  {/* Kalemler */}
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>
                        <i className="fas fa-list" style={{ marginRight: 6, color: '#3b82f6' }}></i>Ödeme Kalemleri
                      </div>
                      {odemePlani.kalemler.length > 0 && (() => {
                        const toplamKalem = odemePlani.kalemler.reduce((s, k) => s + (parseFloat(k.miktar) || 0), 0);
                        const teklifNum = parseFloat(odemePlani.teklifMiktari || '0');
                        const fark = teklifNum - toplamKalem;
                        return (
                          <div style={{ fontSize: 12, color: fark === 0 ? '#059669' : fark > 0 ? '#d97706' : '#ef4444', fontWeight: 600 }}>
                            Toplam: {toplamKalem.toLocaleString('tr-TR')} ₺
                            {teklifNum > 0 && <span style={{ marginLeft: 6, opacity: 0.8 }}>/ Fark: {fark >= 0 ? '+' : ''}{fark.toLocaleString('tr-TR')} ₺</span>}
                          </div>
                        );
                      })()}
                    </div>
                    {odemePlani.kalemler.length > 0 && (
                      <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden', marginBottom: 10 }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                          <thead>
                            <tr style={{ background: '#f8fafc' }}>
                              <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#64748b', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid #e2e8f0', width: 32 }}>#</th>
                              <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#64748b', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid #e2e8f0' }}>Vade Tarihi</th>
                              <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#64748b', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid #e2e8f0' }}>Miktar (₺)</th>
                              <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#64748b', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid #e2e8f0' }}>{t('label.aciklama')}</th>
                              <th style={{ padding: '8px 4px', borderBottom: '1px solid #e2e8f0', width: 32 }}></th>
                            </tr>
                          </thead>
                          <tbody>
                            {odemePlani.kalemler.map((k, i) => (
                              <tr key={i} style={{ borderBottom: i < odemePlani.kalemler.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                                <td style={{ padding: '6px 12px', color: '#94a3b8', fontWeight: 600 }}>{i + 1}</td>
                                <td style={{ padding: '4px 8px' }}>
                                  <TrDateInput value={k.vadeTarihi}
                                    onChange={e => setOdemePlani(prev => { if (!prev) return prev; const ks = [...prev.kalemler]; ks[i] = { ...ks[i], vadeTarihi: e.target.value }; return { ...prev, kalemler: ks }; })}
                                    style={{ padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12.5, color: '#374151', outline: 'none', width: '100%' }}
                                  />
                                </td>
                                <td style={{ padding: '4px 8px' }}>
                                  <input type="text" value={formatCurrency(k.miktar)}
                                    onChange={e => setOdemePlani(prev => { if (!prev) return prev; const ks = [...prev.kalemler]; ks[i] = { ...ks[i], miktar: parseCurrency(e.target.value) }; return { ...prev, kalemler: ks }; })}
                                    placeholder="0"
                                    style={{ padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12.5, color: '#374151', outline: 'none', width: '100%' }}
                                  />
                                </td>
                                <td style={{ padding: '4px 8px' }}>
                                  <input type="text" value={k.aciklama}
                                    onChange={e => setOdemePlani(prev => { if (!prev) return prev; const ks = [...prev.kalemler]; ks[i] = { ...ks[i], aciklama: e.target.value }; return { ...prev, kalemler: ks }; })}
                                    placeholder="—"
                                    style={{ padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12.5, color: '#374151', outline: 'none', width: '100%' }}
                                  />
                                </td>
                                <td style={{ padding: '4px 8px', textAlign: 'center' }}>
                                  <button onClick={() => setOdemePlani(prev => { if (!prev) return prev; const ks = prev.kalemler.filter((_, idx) => idx !== i); return { ...prev, kalemler: ks }; })}
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 4, borderRadius: 4 }}
                                    onMouseEnter={e => (e.currentTarget.style.color = '#ef4444')}
                                    onMouseLeave={e => (e.currentTarget.style.color = '#94a3b8')}>
                                    <i className="fas fa-times" style={{ fontSize: 13 }}></i>
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    <button
                      onClick={() => setOdemePlani(prev => prev ? { ...prev, kalemler: [...prev.kalemler, { kalemID: null, vadeTarihi: '', miktar: '', aciklama: '' }] } : prev)}
                      style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', border: '1px dashed #cbd5e1', borderRadius: 7, background: 'transparent', fontSize: 12.5, color: '#64748b', cursor: 'pointer', fontWeight: 500 }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = '#3b82f6'; e.currentTarget.style.color = '#3b82f6'; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.color = '#64748b'; }}>
                      <i className="fas fa-plus" style={{ fontSize: 11 }}></i> Kalem Ekle
                    </button>
                  </div>

                  {/* Toplu Planlama */}
                  <div style={{ border: '1px solid #e2e8f0', borderRadius: 12, padding: '14px 16px', background: '#fafafa' }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 12 }}>
                      <i className="fas fa-magic" style={{ marginRight: 6, color: '#8b5cf6' }}></i>Toplu Planlama
                    </div>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b' }}>Planlanacak Tutar (₺)</label>
                        <input type="text" value={formatCurrency(topluPlanTutari)} onChange={e => setTopluPlanTutari(parseCurrency(e.target.value))}
                          placeholder="0"
                          style={{ padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13, width: 120, outline: 'none' }} />
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b' }}>Taksit Sayısı</label>
                        <input type="number" min="1" value={topluTaksitSayisi} onChange={e => setTopluTaksitSayisi(e.target.value)}
                          style={{ padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13, width: 90, outline: 'none' }} />
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b' }}>İlk Vade Tarihi</label>
                        <TrDateInput value={topluIlkTarih} onChange={e => setTopluIlkTarih(e.target.value)}
                          style={{ padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13, outline: 'none' }} />
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 140 }}>
                        <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b' }}>Açıklama Şablonu</label>
                        <input type="text" value={topluAciklama} onChange={e => setTopluAciklama(e.target.value)}
                          placeholder={t('label.ara')}
                          style={{ padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13, outline: 'none' }} />
                      </div>
                    </div>
                    <button onClick={handleTopluPlanOlustur}
                      style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', border: 'none', borderRadius: 8, background: '#8b5cf6', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                      onMouseEnter={e => (e.currentTarget.style.background = '#7c3aed')}
                      onMouseLeave={e => (e.currentTarget.style.background = '#8b5cf6')}>
                      <i className="fas fa-magic" style={{ fontSize: 12 }}></i> Taksitleri Oluştur
                    </button>
                    <div style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 8 }}>Mevcut kalemler temizlenerek yenileri oluşturulur. Planlanacak tutar eşit taksitlere bölünür. Ortalama vade gün olarak hesaplanır.</div>
                  </div>
                </>
              )}
            </div>

            {/* Footer */}
            <div style={{ padding: '12px 20px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: 10, background: '#fafafa', flexShrink: 0 }}>
              <button onClick={() => setShowOdemePlaniModal(null)}
                style={{ padding: '8px 18px', border: '1px solid #d1d5db', borderRadius: 8, background: '#fff', fontSize: 13.5, cursor: 'pointer', fontWeight: 500 }}>
                İptal
              </button>
              <button onClick={handleOdemePlaniSave} disabled={odemePlaniSaving || odemePlaniLoading || !odemePlani}
                style={{ padding: '8px 20px', border: 'none', borderRadius: 8, background: odemePlaniSaving ? '#94a3b8' : '#1a2440', color: '#fff', fontSize: 13.5, cursor: odemePlaniSaving ? 'not-allowed' : 'pointer', fontWeight: 600 }}
                onMouseEnter={e => { if (!odemePlaniSaving) e.currentTarget.style.background = '#e67d21'; }}
                onMouseLeave={e => { if (!odemePlaniSaving) e.currentTarget.style.background = '#1a2440'; }}>
                <i className="fas fa-save" style={{ marginRight: 6 }}></i>
                {odemePlaniSaving ? 'Kaydediliyor...' : 'Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Müşteri Linki Modalı */}
      {showLinkModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 700, maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 40px rgba(0,0,0,0.25)' }}>
            {/* Header */}
            <div style={{ padding: '16px 24px', borderBottom: '1px solid #e2e8f0', background: '#1a2440', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h2 style={{ fontSize: 16, fontWeight: 700, color: '#fff', margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                <i className="fas fa-link" style={{ fontSize: 15 }} />
                Müşteri Portal Linkleri
              </h2>
              <button onClick={() => setShowLinkModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.7)', padding: 6 }} onMouseEnter={e => (e.currentTarget.style.color = '#fff')} onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.7)')}>
                <i className="fas fa-times" style={{ fontSize: 18 }}></i>
              </button>
            </div>

            {/* Body */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
              {linklerLoading ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>
                  <i className="fas fa-spinner fa-spin" style={{ fontSize: 24 }}></i>
                  <div style={{ marginTop: 8, fontSize: 13 }}>{t('msg.yukleniyor')}</div>
                </div>
              ) : (
                <>
                  {/* Aktif Linkler */}
                  {linkler.length > 0 ? (
                    <div style={{ marginBottom: 24 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 12 }}>
                        <i className="fas fa-list" style={{ marginRight: 6, color: '#3b82f6' }}></i>Aktif Linkler
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {linkler.map(link => (
                          <div key={link.linkID} style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 16px', background: link.sureGectiMi ? '#fef2f2' : '#f9fafb' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                              <div style={{ fontSize: 11.5, color: '#64748b', fontWeight: 600 }}>
                                <i className="far fa-calendar-alt" style={{ marginRight: 4 }}></i>
                                Oluşturulma: {new Date(link.olusturmaTarihi).toLocaleDateString('tr-TR')}
                                {' • '}
                                <span style={{ color: link.sureGectiMi ? '#ef4444' : '#10b981' }}>
                                  Geçerlilik: {new Date(link.gecerlilikTarihi).toLocaleDateString('tr-TR')}
                                  {link.sureGectiMi && ' (Süresi Doldu)'}
                                </span>
                              </div>
                              {link.sureGectiMi && (
                                <span style={{ fontSize: 11, background: '#fee2e2', color: '#dc2626', padding: '3px 8px', borderRadius: 6, fontWeight: 600 }}>
                                  <i className="fas fa-exclamation-triangle" style={{ marginRight: 4 }}></i>Süresi Geçmiş
                                </span>
                              )}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, marginBottom: 10 }}>
                              <i className="fas fa-link" style={{ fontSize: 12, color: '#94a3b8' }}></i>
                              <input type="text" value={link.portalUrl} readOnly style={{ flex: 1, border: 'none', outline: 'none', fontSize: 12.5, color: '#475569', background: 'transparent' }} />
                              <button onClick={() => handleCopyLink(link.linkID, link.portalUrl)} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '5px 12px', border: '1px solid #cbd5e1', borderRadius: 6, background: copiedLinkId === link.linkID ? '#10b981' : '#fff', color: copiedLinkId === link.linkID ? '#fff' : '#475569', fontSize: 11.5, fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s' }}>
                                <i className={copiedLinkId === link.linkID ? 'fas fa-check' : 'far fa-copy'} style={{ fontSize: 11 }}></i>
                                {copiedLinkId === link.linkID ? 'Kopyalandı' : 'Kopyala'}
                              </button>
                              <button
                                onClick={() => setQrVisibleLinkId(qrVisibleLinkId === link.linkID ? null : link.linkID)}
                                style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '5px 12px', border: '1px solid #cbd5e1', borderRadius: 6, background: qrVisibleLinkId === link.linkID ? '#1a2440' : '#fff', color: qrVisibleLinkId === link.linkID ? '#fff' : '#475569', fontSize: 11.5, fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s' }}
                              >
                                <i className="fas fa-qrcode" style={{ fontSize: 12 }}></i>
                                QR Kod
                              </button>
                              {sunum.cari?.telefon1 && (() => {
                                const telefon = sunum.cari!.telefon1.replace(/\D/g, '');
                                const numara = telefon.startsWith('0') ? '90' + telefon.slice(1) : telefon.startsWith('90') ? telefon : '90' + telefon;
                                const mesaj = encodeURIComponent(`Merhaba ${sunum.cari!.adiSoyadi}, size özel hazırladığımız sunum için aşağıdaki bağlantıyı kullanabilirsiniz:\n${link.portalUrl}`);
                                return (
                                  <a
                                    href={`https://web.whatsapp.com/send?phone=${numara}&text=${mesaj}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '5px 12px', border: '1px solid #22c55e', borderRadius: 6, background: '#f0fdf4', color: '#16a34a', fontSize: 11.5, fontWeight: 600, cursor: 'pointer', textDecoration: 'none', transition: 'all 0.15s' }}
                                    onMouseEnter={e => { e.currentTarget.style.background = '#dcfce7'; }}
                                    onMouseLeave={e => { e.currentTarget.style.background = '#f0fdf4'; }}
                                  >
                                    <i className="fab fa-whatsapp" style={{ fontSize: 13 }}></i>
                                    WhatsApp
                                  </a>
                                );
                              })()}
                            </div>
                            {qrVisibleLinkId === link.linkID && (
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '14px 0 6px', borderTop: '1px dashed #e2e8f0', marginTop: 4 }}>
                                <QRCodeSVG
                                  value={link.portalUrl}
                                  size={150}
                                  bgColor="#ffffff"
                                  fgColor="#1a2440"
                                  level="M"
                                  marginSize={2}
                                />
                                <span style={{ fontSize: 11.5, color: '#94a3b8' }}>Kamera ile taratın</span>
                              </div>
                            )}
                            <div style={{ display: 'flex', gap: 8 }}>
                              {link.sureGectiMi && (
                                <button onClick={() => handleExtendLink(link.linkID)} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, padding: '6px 12px', border: '1px solid #3b82f6', borderRadius: 7, background: '#eff6ff', color: '#2563eb', fontSize: 12, fontWeight: 600, cursor: 'pointer' }} onMouseEnter={e => { e.currentTarget.style.background = '#dbeafe'; }} onMouseLeave={e => { e.currentTarget.style.background = '#eff6ff'; }}>
                                  <i className="fas fa-clock" style={{ fontSize: 11 }}></i> +3 Gün Uzat
                                </button>
                              )}
                              <button onClick={() => handleDeactivateLink(link.linkID)} style={{ flex: link.sureGectiMi ? 1 : 'initial', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, padding: '6px 12px', border: '1px solid #f87171', borderRadius: 7, background: '#fef2f2', color: '#dc2626', fontSize: 12, fontWeight: 600, cursor: 'pointer' }} onMouseEnter={e => { e.currentTarget.style.background = '#fee2e2'; }} onMouseLeave={e => { e.currentTarget.style.background = '#fef2f2'; }}>
                                <i className="fas fa-ban" style={{ fontSize: 11 }}></i> Devre Dışı Bırak
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '1.5rem', color: '#94a3b8', fontSize: 13, marginBottom: 24 }}>
                      <i className="fas fa-link" style={{ fontSize: 28, display: 'block', marginBottom: 8, opacity: 0.4 }}></i>
                      Henüz oluşturulmuş link yok.
                    </div>
                  )}

                  {/* Yeni Link Oluşturma */}
                  <div style={{ border: '2px dashed #cbd5e1', borderRadius: 12, padding: '16px 18px', background: '#fafafa' }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 12 }}>
                      <i className="fas fa-plus-circle" style={{ marginRight: 6, color: '#10b981' }}></i>Yeni Link Oluştur
                    </div>
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10 }}>
                      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <label style={{ fontSize: 11.5, fontWeight: 600, color: '#64748b' }}>Geçerlilik Süresi (Gün)</label>
                        <input type="number" min="1" value={yeniLinkGun} onChange={e => setYeniLinkGun(e.target.value)} style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, outline: 'none' }} />
                      </div>
                      <button onClick={handleCreateLink} disabled={creatingLink} style={{ padding: '8px 18px', border: 'none', borderRadius: 8, background: creatingLink ? '#94a3b8' : '#10b981', color: '#fff', fontSize: 13, fontWeight: 600, cursor: creatingLink ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 6 }} onMouseEnter={e => { if (!creatingLink) e.currentTarget.style.background = '#059669'; }} onMouseLeave={e => { if (!creatingLink) e.currentTarget.style.background = '#10b981'; }}>
                        <i className={creatingLink ? 'fas fa-spinner fa-spin' : 'fas fa-plus'} style={{ fontSize: 12 }}></i>
                        {creatingLink ? 'Oluşturuluyor...' : 'Oluştur'}
                      </button>
                    </div>
                    <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 8 }}>
                      <i className="fas fa-info-circle" style={{ marginRight: 4 }}></i>
                      Oluşturduğunuz link, belirlenen süre boyunca geçerli olacaktır. Müşteri bu linkle sunumu görüntüleyebilir ve tepki verebilir.
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Footer */}
            <div style={{ padding: '12px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', background: '#fafafa' }}>
              <button onClick={() => setShowLinkModal(false)} style={{ padding: '8px 20px', border: '1px solid #d1d5db', borderRadius: 8, background: '#fff', fontSize: 13.5, cursor: 'pointer', fontWeight: 500 }}>
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Döküman Paylaşım Modal */}
      {showDokumanPaylas && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1200, background: 'rgba(0,0,0,0.45)', display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: 14, boxShadow: '0 8px 40px rgba(0,0,0,0.22)', width: '100%', maxWidth: 620, maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h4 style={{ margin: 0, fontWeight: 700, fontSize: 15 }}>Döküman Paylaş</h4>
              <button onClick={() => setShowDokumanPaylas(false)} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#64748b' }}>×</button>
            </div>
            <div style={{ padding: '12px 20px', borderBottom: '1px solid #f1f5f9' }}>
              <select
                value={dokumanEtiketFiltre}
                onChange={e => setDokumanEtiketFiltre(e.target.value === '' ? '' : parseInt(e.target.value))}
                style={{ padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13, outline: 'none', minWidth: 180 }}
              >
                <option value=''>Tüm Etiketler</option>
                {tumEtiketler.map(et => (
                  <option key={et.etiketID} value={et.etiketID}>{et.ad}</option>
                ))}
              </select>
              <span style={{ marginLeft: 12, fontSize: 12.5, color: '#64748b' }}>{seciliDokumanIDs.length} döküman seçildi</span>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '12px 20px', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {tumDokumanlar
                .filter(d => dokumanEtiketFiltre === '' || d.etiketler.some(e => e.etiketID === dokumanEtiketFiltre))
                .map(doc => {
                  const secili = seciliDokumanIDs.includes(doc.dokumanID);
                  return (
                    <label key={doc.dokumanID} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 12px', border: `1px solid ${secili ? '#6366f1' : '#e2e8f0'}`, borderRadius: 9, background: secili ? '#f0f0ff' : '#fafafa', cursor: 'pointer', transition: 'all 0.12s' }}>
                      <input
                        type='checkbox'
                        checked={secili}
                        onChange={() => setSeciliDokumanIDs(prev => secili ? prev.filter(x => x !== doc.dokumanID) : [...prev, doc.dokumanID])}
                        style={{ marginTop: 3, accentColor: '#6366f1', cursor: 'pointer', width: 15, height: 15 }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: 13.5, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 6 }}>
                          <i className={doc.dokumanTipi === 'Video' ? 'fas fa-film' : 'fas fa-file-alt'} style={{ color: '#6366f1', fontSize: 12 }} />
                          {doc.baslik}
                        </div>
                        {doc.aciklama && <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{doc.aciklama}</div>}
                        {doc.etiketler?.length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, marginTop: 4 }}>
                            {doc.etiketler.map((e, i) => (
                              <span key={i} style={{ background: e.renk || '#e2e8f0', color: '#fff', borderRadius: 5, padding: '0 6px', fontSize: 10.5 }}>{e.ad}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    </label>
                  );
                })}
              {tumDokumanlar.filter(d => dokumanEtiketFiltre === '' || d.etiketler.some(e => e.etiketID === dokumanEtiketFiltre)).length === 0 && (
                <div style={{ color: '#94a3b8', textAlign: 'center', padding: 24 }}>Bu etikete ait döküman yok.</div>
              )}
            </div>
            <div style={{ padding: '12px 20px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setShowDokumanPaylas(false)} style={{ padding: '8px 18px', border: '1px solid #d1d5db', borderRadius: 8, background: '#fff', fontSize: 13.5, cursor: 'pointer' }}>{t('btn.iptal')}</button>
              <button onClick={handleDokumanPaylasimKaydet} disabled={dokumanPaylasimSaving} style={{ padding: '8px 20px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13.5, fontWeight: 600, cursor: dokumanPaylasimSaving ? 'not-allowed' : 'pointer', opacity: dokumanPaylasimSaving ? 0.75 : 1 }}>
                {dokumanPaylasimSaving ? 'Kaydediliyor...' : 'Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmlakSunumlariDetay;
